<template>
  <div class="surgery-info-page">
    <!-- Header with Go Back -->
    <b-card no-body class="header-card mb-2">
      <b-card-body class="p-2">
        <div class="d-flex align-items-center justify-content-between">
          <div class="d-flex align-items-center">
            <b-button
              variant="link"
              class="go-back-btn p-0 mr-3"
              @click="$router.go(-1)"
            >
              <feather-icon icon="ArrowLeftIcon" size="20" />
            </b-button>
            <h4 class="mb-0">
              {{ $t("reception.Main Patient Surgery Information") }}
            </h4>
          </div>
          <div class="action-buttons">
            <!-- Quick Actions -->
            <b-button
              v-if="
                $store.state.app.permissions.includes('get-patientCardTest') &&
                !updateSurgeryInfoIndicator
              "
              variant="primary"
              size="sm"
              class="mr-2"
              @click="$bvModal.show('modal-add-lab-test')"
            >
              <feather-icon icon="DropletIcon" size="14" class="mr-1" />
              {{ $t("reception.add-lab-test") }}
            </b-button>
            <b-button
              v-if="!updateSurgeryInfoIndicator"
              variant="primary"
              size="sm"
              class="mr-2"
              @click="$bvModal.show('modal-add-drug')"
            >
              <feather-icon icon="PackageIcon" size="14" class="mr-1" />
              {{ $t("reception.add-drug") }}
            </b-button>
            <b-button
              v-if="
                $store.state.app.permissions.includes(
                  'get-patientCardRadiology'
                ) && !updateSurgeryInfoIndicator
              "
              variant="primary"
              size="sm"
              class="mr-2"
              @click="$bvModal.show('modal-add-radiology')"
            >
              <feather-icon icon="ImageIcon" size="14" class="mr-1" />
              {{ $t("reception.add-radiology") }}
            </b-button>
            <b-button
              v-if="!updateSurgeryInfoIndicator"
              variant="primary"
              size="sm"
              class="mr-2"
              @click="$bvModal.show('modal-add-blood-transfusion')"
            >
              <feather-icon icon="DropletIcon" size="14" class="mr-1" />
              {{ $t("bloodBank.add-blood-transfusion") }}
            </b-button>
            <b-button
              v-if="
                !updateSurgeryInfoIndicator &&
                $store.state.app.permissions.includes('store-surgery')
              "
              @click="navigateWithDoctors()"
              variant="primary"
              size="sm"
              class="mr-2"
            >
              <feather-icon icon="PlusIcon" size="14" class="mr-1" />
              {{ $t("surgery.Add sub-surgery") }}
            </b-button>
            <b-dropdown
              variant="success"
              size="sm"
              class="mr-2"
              text="Print"
              right
            >
              <template #button-content>
                <feather-icon icon="PrinterIcon" size="14" class="mr-1" />
                {{ $t("common.print") }}
              </template>
              <b-dropdown-item @click="printOperativeNotes()">
                <feather-icon icon="FileTextIcon" size="14" class="mr-1" />
                {{ $t("surgery.print-operative-notes") }}
              </b-dropdown-item>
              <b-dropdown-item @click="openAgreementPrintModal()">
                <feather-icon icon="FileIcon" size="14" class="mr-1" />
                {{ $t("reception.print-agreement") }}
              </b-dropdown-item>
              <b-dropdown-item @click="printDischargeAgainstAdvice()">
                <feather-icon icon="FileTextIcon" size="14" class="mr-1" />
                {{ $t("surgery.print-discharge-against-advice") }}
              </b-dropdown-item>
            </b-dropdown>
            <b-button
              v-if="!updateSurgeryInfoIndicator"
              @click="navigateToEditSurgery()"
              variant="warning"
              size="sm"
              class="mr-2"
            >
              <feather-icon icon="EditIcon" size="14" class="mr-1" />
              {{ $t("surgery.Update Surgery Info") }}
            </b-button>
            <div v-else class="d-inline-flex">
              <b-button
                @click="updateSurgeryInfo()"
                variant="warning"
                size="sm"
                class="mr-2"
              >
                {{ $t("common.submit") }}
              </b-button>
              <b-button
                @click="
                  updateSurgeryInfoIndicator = !updateSurgeryInfoIndicator
                "
                variant="secondary"
                size="sm"
                class="mr-2"
              >
                {{ $t("common.cancel") }}
              </b-button>
            </div>
            <!-- <b-button variant="warning" size="sm" @click="changeSurgery()">
              <feather-icon icon="RefreshCwIcon" size="14" class="mr-1" />
              {{ $t("common.change-surgery") }}
            </b-button> -->
          </div>
        </div>
      </b-card-body>
    </b-card>

    <!-- Main Content -->
    <div class="main-content-wrapper">
      <b-card no-body class="main-content-card">
        <b-card-body class="p-2">
          <template v-if="!updateSurgeryInfoIndicator && patientSurgeryData">
            <!-- Patient & Surgery Info Card (Merged) -->
            <b-card no-body class="info-card mb-2">
              <b-card-header class="section-header">
                <h5 class="mb-0">
                  <feather-icon icon="UserIcon" size="18" class="mr-2" />
                  {{ $t("reception.Main Patient Surgery Information") }}
                </h5>
              </b-card-header>
              <b-card-body class="p-2">
                <div class="info-list">
                  <div class="info-row" v-if="patientSurgeryData.patient_card">
                    <label class="info-label">
                      {{ $t("common.patient-name") }}
                    </label>
                    <p class="info-value">
                      {{ patientSurgeryData.patient_card.patient.name }}
                    </p>
                  </div>
                  <div class="info-row" v-if="patientSurgeryData.surgery">
                    <label class="info-label">
                      {{ $t("common.patient-surgery") }}
                    </label>
                    <p class="info-value">
                      {{ patientSurgeryData.surgery.title }}
                    </p>
                  </div>
                  <div class="info-row" v-if="patientSurgeryData.patient_case">
                    <label class="info-label">
                      {{ $t("emergency.Patient Case") }}
                    </label>
                    <p class="info-value">
                      {{ patientSurgeryData.patient_case }}
                    </p>
                  </div>
                  <div class="info-row" v-if="patientSurgeryData.diagnosis">
                    <label class="info-label">
                      {{ $t("emergency.Diagnosis") }}
                    </label>
                    <p class="info-value">
                      {{ patientSurgeryData.diagnosis }}
                    </p>
                  </div>
                  <div class="info-row" v-if="patientSurgeryData.investigation">
                    <label class="info-label">
                      {{ $t("emergency.Investigation") }}
                    </label>
                    <p class="info-value">
                      {{ patientSurgeryData.investigation }}
                    </p>
                  </div>
                  <div class="info-row" v-if="patientSurgeryData.treatment">
                    <label class="info-label">
                      {{ $t("emergency.Treatment") }}
                    </label>
                    <p class="info-value">
                      {{ patientSurgeryData.treatment }}
                    </p>
                  </div>
                  <div class="info-row" v-if="patientSurgeryData.examination">
                    <label class="info-label">
                      {{ $t("emergency.Examination") }}
                    </label>
                    <p class="info-value">
                      {{ patientSurgeryData.examination }}
                    </p>
                  </div>
                  <div class="info-row" v-if="patientSurgeryData.procedure">
                    <label class="info-label">
                      {{ $t("surgery.procedure") }}
                    </label>
                    <p class="info-value">
                      {{ patientSurgeryData.procedure }}
                    </p>
                  </div>
                  <div class="info-row" v-if="patientSurgeryData.final_result">
                    <label class="info-label">
                      {{ $t("common.final-result") }}
                    </label>
                    <p class="info-value">
                      <b-badge
                        :variant="
                          patientSurgeryData.final_result === 'success'
                            ? 'success'
                            : 'danger'
                        "
                      >
                        {{
                          patientSurgeryData.final_result === "success"
                            ? $t("common.success-operation")
                            : $t("common.failed-operation")
                        }}
                      </b-badge>
                    </p>
                  </div>
                  <div class="info-row" v-if="patientSurgeryData.updated_at">
                    <label class="info-label">
                      {{ $t("reception.patient-updates") }}
                    </label>
                    <p
                      class="info-value"
                      style="unicode-bidi: isolate"
                      :style="{
                        direction: $store.state.appConfig.isRTL ? 'rtl' : 'ltr',
                      }"
                    >
                      {{ patientSurgeryData.updated_at }}
                    </p>
                  </div>
                  <div class="info-row" v-if="patientSurgeryData.created_at">
                    <label class="info-label">
                      {{ $t("reception.patient-info-created-at") }}
                    </label>
                    <p
                      class="info-value"
                      style="unicode-bidi: isolate"
                      :style="{
                        direction: $store.state.appConfig.isRTL ? 'rtl' : 'ltr',
                      }"
                    >
                      {{ patientSurgeryData.created_at }}
                    </p>
                  </div>
                </div>
              </b-card-body>
            </b-card>
          </template>
          <template v-else>
            <b-card-text>
              <b-row>
                <b-col md="12">
                  <div>
                    <b-form-group :label="$t('common.date')" label-for="Date">
                      <datepicker
                        v-model="updateSurgeryData.surgery_date_time"
                        :calendar-button="true"
                        :clear-button="true"
                        :bootstrap-styling="true"
                        style="height: 5rem"
                        class="datebtn2"
                        :placeholder="$t('common.date')"
                      ></datepicker>
                    </b-form-group>
                  </div>
                </b-col>
                <b-col cols="6">
                  <b-form-group
                    :label="$t('surgery.patient_case')"
                    label-for="Case_Details"
                  >
                    <b-form-textarea
                      v-model="updateSurgeryData.patient_case"
                      :placeholder="$t('surgery.patient_case')"
                    />
                  </b-form-group>
                </b-col>

                <b-col cols="6">
                  <b-form-group :label="$t('surgery.diagnosis')">
                    <b-form-textarea
                      v-model="updateSurgeryData.diagnosis"
                      :placeholder="$t('surgery.diagnosis')"
                    />
                  </b-form-group>
                </b-col>
                <b-col cols="12">
                  <b-form-group :label="$t('surgery.patient_case_details')">
                    <b-form-textarea
                      v-model="updateSurgeryData.patient_case_details"
                      :placeholder="$t('surgery.patient_case_details')"
                    />
                  </b-form-group>
                </b-col>
                <b-col cols="12">
                  <b-form-group
                    :label="$t('surgery.procedure')"
                    label-for="Case_Details"
                  >
                    <b-form-textarea
                      id="procedure"
                      v-model="updateSurgeryData.procedure"
                      :placeholder="$t('surgery.procedure')"
                    />
                  </b-form-group>
                </b-col>
                <b-col cols="12">
                  <b-form-group
                    :label="$t('common.filter-by-final-result')"
                    label-for="final_result"
                  >
                    <v-select
                      id="final_result"
                      v-model="updateSurgeryData.final_result"
                      :options="final_result_options"
                      :reduce="(option) => option.value"
                      :placeholder="$t('common.filter-by-final-result')"
                    />
                  </b-form-group>
                </b-col>
              </b-row>
            </b-card-text>
          </template>
          <!-- Surgery Doctors Card View -->
          <template v-if="!updateSurgeryInfoIndicator && patientSurgeryData">
            <b-card no-body class="info-card mb-2">
              <b-card-header class="section-header">
                <div
                  class="d-flex justify-content-between align-items-center header-actions-wrapper"
                >
                  <h5 class="mb-0">
                    <feather-icon icon="UserCheckIcon" size="18" class="mr-2" />
                    {{ $t("surgery.surgery-doctor") }}
                  </h5>
                  <div class="d-flex align-items-center gap-2">
                    <span
                      class="count-badge"
                      v-if="
                        patientSurgeryData.doctors_prices &&
                        patientSurgeryData.doctors_prices.items
                      "
                    >
                      {{ patientSurgeryData.doctors_prices.items.length }}
                    </span>
                    <b-button
                      variant="primary"
                      size="sm"
                      @click="openEditDoctorSurgeryModal()"
                    >
                      <feather-icon icon="EditIcon" size="14" class="mr-1" />
                      {{ $t("common.edit") }}
                    </b-button>
                  </div>
                </div>
              </b-card-header>
              <b-card-body class="p-2">
                <div
                  v-if="
                    patientSurgeryData.doctors_prices &&
                    patientSurgeryData.doctors_prices.items &&
                    patientSurgeryData.doctors_prices.items.length > 0
                  "
                  class="modern-cards-grid"
                >
                  <div
                    v-for="doctor in patientSurgeryData.doctors_prices.items"
                    :key="doctor.id"
                    class="modern-card"
                  >
                    <div class="modern-card-header">
                      <div class="modern-card-icon">
                        <feather-icon
                          icon="UserIcon"
                          size="24"
                          class="text-primary"
                        />
                      </div>
                      <div class="modern-card-title">
                        {{ doctor.doctor.name }}
                      </div>
                    </div>
                    <div class="modern-card-content">
                      <div
                        class="modern-card-info-item"
                        v-if="doctor.doctor.phone"
                      >
                        <feather-icon icon="PhoneIcon" size="14" />
                        <span>{{ doctor.doctor.phone }}</span>
                      </div>
                    </div>
                    <div class="modern-card-footer">
                      <b-badge
                        :variant="
                          doctor.type === 'surgeon' ? 'primary' : 'info'
                        "
                      >
                        {{
                          doctor.type === "surgeon"
                            ? $t("hospital.surgeon")
                            : $t("hospital.anesthetist")
                        }}
                      </b-badge>
                    </div>
                  </div>
                </div>
                <div v-else class="no-data-empty">
                  <feather-icon
                    icon="UserCheckIcon"
                    size="48"
                    class="empty-icon"
                  />
                  <p>{{ $t("common.no-data-available") }}</p>
                </div>
              </b-card-body>
            </b-card>

            <!-- Doctor Assistants Card View -->
            <b-card no-body class="info-card mb-2">
              <b-card-header class="section-header">
                <div
                  class="d-flex justify-content-between align-items-center header-actions-wrapper"
                >
                  <h5 class="mb-0">
                    <feather-icon icon="UsersIcon" size="18" class="mr-2" />
                    {{ $t("surgery.doctor-assistants") }}
                  </h5>
                  <div class="d-flex align-items-center gap-2">
                    <span
                      class="count-badge"
                      v-if="patientSurgeryData.doctor_assist"
                    >
                      {{ patientSurgeryData.doctor_assist.length }}
                    </span>
                    <b-button
                      variant="primary"
                      size="sm"
                      @click="openEditDoctorAssistantsModal()"
                    >
                      <feather-icon icon="EditIcon" size="14" class="mr-1" />
                      {{ $t("common.edit") }}
                    </b-button>
                  </div>
                </div>
              </b-card-header>
              <b-card-body class="p-2">
                <div
                  v-if="
                    patientSurgeryData.doctor_assist &&
                    patientSurgeryData.doctor_assist.length > 0
                  "
                  class="modern-cards-grid"
                >
                  <div
                    v-for="assistant in patientSurgeryData.doctor_assist"
                    :key="assistant.id"
                    class="modern-card"
                  >
                    <div class="modern-card-header">
                      <div class="modern-card-icon">
                        <feather-icon
                          icon="UserIcon"
                          size="24"
                          class="text-success"
                        />
                      </div>
                      <div class="modern-card-title">
                        {{ assistant.name }}
                      </div>
                    </div>
                    <div class="modern-card-content">
                      <div class="modern-card-info-item" v-if="assistant.phone">
                        <feather-icon icon="PhoneIcon" size="14" />
                        <span>{{ assistant.phone }}</span>
                      </div>
                    </div>
                    <div class="modern-card-footer">
                      <b-badge
                        :variant="
                          assistant.type === 'surgeon_assistant'
                            ? 'warning'
                            : 'info'
                        "
                      >
                        {{
                          assistant.type === "surgeon_assistant"
                            ? $t("hospital.surgeon-assistant")
                            : $t("hospital.anesthetist-assistant")
                        }}
                      </b-badge>
                    </div>
                  </div>
                </div>
                <div v-else class="no-data-empty">
                  <feather-icon icon="UsersIcon" size="48" class="empty-icon" />
                  <p>{{ $t("common.no-data-available") }}</p>
                </div>
              </b-card-body>
            </b-card>
          </template>
          <template v-if="!updateSurgeryInfoIndicator && patientSurgeryData">
            <PatientSurgeryStaff
              :patientCardId="patientSurgeryData.patient_card.id"
              :doctorSurgeryId="patientSurgeryData.id"
              :staff="patientSurgeryData.surgery_staff"
              @getData="getPatientSurgeryInfo"
            />
            <DoctorAssist
              :patientCardId="patientSurgeryData.patient_card.id"
              :doctorSurgeryId="patientSurgeryData.id"
              :doctorAssist="patientSurgeryData.doctor_assist"
              @getData="getPatientSurgeryInfo"
            />
            <PatientSurgeyConsumables
              :patientCardId="patientSurgeryData.patient_card.id"
              :patientName="patientSurgeryData.patient_card.patient.name"
              :doctorSurgeryId="patientSurgeryData.id"
              @getData="getPatientSurgeryInfo"
            />
          </template>
        </b-card-body>
      </b-card>

      <SurgeryInvoice
        v-if="patientSurgeryData && patientSurgeryData.id"
        :patientSurgeryData="patientSurgeryData"
        :doctorSurgeryId="$route.params.id"
      />
    </div>

    <!-- Patient Card Tests Section -->
    <b-card
      v-if="
        patientSurgeryData &&
        patientSurgeryData.patient_card &&
        $store.state.app.permissions.includes('get-patientCardTest')
      "
      no-body
      class="info-card mb-2"
    >
      <b-card-header class="section-header">
        <div class="d-flex justify-content-between align-items-center">
          <h5 class="mb-0">
            <feather-icon icon="DropletIcon" size="18" class="mr-2" />
            {{ $t("lab.tests") }}
          </h5>
          <span class="count-badge">{{ patientTests.length }}</span>
        </div>
      </b-card-header>
      <b-card-body class="p-2">
        <div v-if="patientTests.length === 0" class="no-data-empty">
          <feather-icon icon="DropletIcon" size="48" class="empty-icon" />
          <p>{{ $t("common.no-data-available") }}</p>
        </div>
        <div v-else class="modern-cards-grid">
          <div v-for="test in patientTests" :key="test.id" class="modern-card">
            <div class="modern-card-header">
              <div class="modern-card-icon">
                <feather-icon
                  icon="DropletIcon"
                  size="24"
                  class="text-primary"
                />
              </div>
              <div class="modern-card-title">
                {{ test.test ? test.test.name : "-" }}
              </div>
            </div>
            <div class="modern-card-content">
              <div class="modern-card-info-item" v-if="test.sample_id">
                <feather-icon icon="HashIcon" size="14" />
                <span>{{ $t("lab.sample-id") }}: {{ test.sample_id }}</span>
              </div>
              <div class="modern-card-info-item" v-if="test.result">
                <feather-icon icon="FileTextIcon" size="14" />
                <span>{{ $t("lab.result") }}: {{ test.result }}</span>
              </div>
              <div class="modern-card-info-item" v-if="test.date">
                <feather-icon icon="CalendarIcon" size="14" />
                <span>{{ test.date }}</span>
              </div>
            </div>
            <div class="modern-card-footer" v-if="test.group">
              <b-badge variant="info">
                {{ $t("lab.test-group") }}: {{ test.group.name }}
              </b-badge>
            </div>
          </div>
        </div>
      </b-card-body>
    </b-card>

    <!-- Patient Drugs Section -->
    <b-card
      v-if="patientSurgeryData && patientSurgeryData.patient_card"
      no-body
      class="info-card mb-2"
    >
      <b-card-header class="section-header">
        <div class="d-flex justify-content-between align-items-center">
          <h5 class="mb-0">
            <feather-icon icon="PackageIcon" size="18" class="mr-2" />
            {{ $t("pharmacy.drugs") }}
          </h5>
          <span class="count-badge">{{ patientDrugs.length }}</span>
        </div>
      </b-card-header>
      <b-card-body class="p-2">
        <div v-if="patientDrugs.length === 0" class="no-data-empty">
          <feather-icon icon="PackageIcon" size="48" class="empty-icon" />
          <p>{{ $t("common.no-data-available") }}</p>
        </div>
        <div v-else class="modern-cards-grid">
          <div v-for="drug in patientDrugs" :key="drug.id" class="modern-card">
            <div class="modern-card-header">
              <div class="modern-card-icon">
                <feather-icon
                  icon="PackageIcon"
                  size="24"
                  class="text-success"
                />
              </div>
              <div class="modern-card-title">
                {{ drug.item ? drug.item.name : drug.item_name || "-" }}
              </div>
            </div>
            <div class="modern-card-content">
              <div
                class="modern-card-info-item"
                v-if="drug.quantity || drug.number_of_doses"
              >
                <feather-icon icon="LayersIcon" size="14" />
                <span>{{
                  drug.quantity
                    ? $t("pharmacy.Quantity") + ": " + drug.quantity
                    : $t("office.number_of_doses") + ": " + drug.number_of_doses
                }}</span>
              </div>
              <div class="modern-card-info-item" v-if="drug.dose">
                <feather-icon icon="DropletIcon" size="14" />
                <span>{{ $t("pharmacy.Dose") }}: {{ drug.dose }}</span>
              </div>
              <div
                class="modern-card-info-item"
                v-if="drug.duration_between_doses"
              >
                <feather-icon icon="ClockIcon" size="14" />
                <span
                  >{{ $t("office.duration_between_doses") }}:
                  {{ drug.duration_between_doses }}</span
                >
              </div>
              <div class="modern-card-info-item" v-if="drug.created_at">
                <feather-icon icon="CalendarIcon" size="14" />
                <span>{{ drug.created_at }}</span>
              </div>
            </div>
            <div class="modern-card-footer" v-if="drug.source_of_drug">
              <b-badge
                :variant="
                  drug.source_of_drug === 'hospital'
                    ? 'primary'
                    : drug.source_of_drug === 'pharmacy'
                    ? 'success'
                    : 'warning'
                "
              >
                {{ drug.source_of_drug }}
              </b-badge>
            </div>
          </div>
        </div>
      </b-card-body>
    </b-card>

    <!-- Patient Radiology Section -->
    <b-card
      v-if="
        patientSurgeryData &&
        patientSurgeryData.patient_card &&
        $store.state.app.permissions.includes('get-patientCardRadiology')
      "
      no-body
      class="info-card mb-2"
    >
      <b-card-header class="section-header">
        <div class="d-flex justify-content-between align-items-center">
          <h5 class="mb-0">
            <feather-icon icon="ImageIcon" size="18" class="mr-2" />
            {{ $t("radiology.radiology") }}
          </h5>
          <span class="count-badge">{{ patientRadiology.length }}</span>
        </div>
      </b-card-header>
      <b-card-body class="p-2">
        <div v-if="patientRadiology.length === 0" class="no-data-empty">
          <feather-icon icon="ImageIcon" size="48" class="empty-icon" />
          <p>{{ $t("common.no-data-available") }}</p>
        </div>
        <div v-else class="modern-cards-grid">
          <div
            v-for="radiology in patientRadiology"
            :key="radiology.id"
            class="modern-card"
          >
            <div class="modern-card-header">
              <div class="modern-card-icon">
                <feather-icon icon="ImageIcon" size="24" class="text-info" />
              </div>
              <div class="modern-card-title">
                {{ radiology.radiology ? radiology.radiology.name : "-" }}
              </div>
            </div>
            <div class="modern-card-content">
              <div class="modern-card-info-item" v-if="radiology.url">
                <feather-icon icon="LinkIcon" size="14" />
                <a :href="radiology.url" target="_blank" class="text-primary">
                  {{ $t("common.view") }}
                </a>
              </div>
              <div class="modern-card-info-item" v-if="radiology.created_at">
                <feather-icon icon="CalendarIcon" size="14" />
                <span>{{ radiology.created_at }}</span>
              </div>
            </div>
            <div class="modern-card-footer" v-if="radiology.type">
              <b-badge variant="secondary">
                {{ $t("common.type") }}: {{ radiology.type }}
              </b-badge>
            </div>
          </div>
        </div>
      </b-card-body>
    </b-card>

    <!-- Blood Transfusions Section -->
    <b-card
      v-if="patientSurgeryData && patientSurgeryData.patient_card"
      no-body
      class="info-card mb-2"
    >
      <b-card-header class="section-header">
        <div class="d-flex justify-content-between align-items-center">
          <h5 class="mb-0">
            <feather-icon icon="DropletIcon" size="18" class="mr-2" />
            {{ $t("bloodBank.blood-transfusions") }}
          </h5>
          <span class="count-badge">{{ bloodTransfusions.length }}</span>
        </div>
      </b-card-header>
      <b-card-body class="p-2">
        <div v-if="bloodTransfusions.length === 0" class="no-data-empty">
          <feather-icon icon="DropletIcon" size="48" class="empty-icon" />
          <p>{{ $t("common.no-data-available") }}</p>
        </div>
        <div v-else class="modern-cards-grid">
          <div
            v-for="transfusion in bloodTransfusions"
            :key="transfusion.id"
            class="modern-card"
          >
            <div class="modern-card-header">
              <div class="modern-card-icon">
                <feather-icon
                  icon="DropletIcon"
                  size="24"
                  class="text-danger"
                />
              </div>
              <div class="modern-card-title">
                {{ getTransfusionType(transfusion.blood_transfusion_type_id) }}
              </div>
            </div>
            <div class="modern-card-content">
              <div class="modern-card-info-item">
                <feather-icon icon="TagIcon" size="14" />
                <span
                  >{{ $t("bloodBank.patient-abo") }}:
                  {{ transfusion.patient_abo }}</span
                >
              </div>
              <div class="modern-card-info-item">
                <feather-icon icon="TagIcon" size="14" />
                <span
                  >{{ $t("bloodBank.patient-rh") }}:
                  {{ transfusion.patient_rh }}</span
                >
              </div>
            </div>
            <div class="modern-card-footer">
              <b-badge variant="info">
                {{ $t("bloodBank.number-of-units") }}:
                {{ transfusion.number_of_units }}
              </b-badge>
            </div>
          </div>
        </div>
      </b-card-body>
    </b-card>

    <!-- Invoices Section (Read-only) -->
    <b-card
      v-if="
        patientSurgeryData &&
        patientSurgeryData.patient_card &&
        $store.state.app.permissions.includes('get-invoice')
      "
      no-body
      class="info-card mb-2"
    >
      <b-card-header class="section-header">
        <div class="d-flex justify-content-between align-items-center">
          <h5 class="mb-0">
            <feather-icon icon="FileTextIcon" size="18" class="mr-2" />
            {{ $t("incubator.invoices") }}
          </h5>
          <span class="count-badge">{{ invoices.length }}</span>
        </div>
      </b-card-header>
      <b-card-body class="p-2">
        <div v-if="invoices && invoices.length" class="invoices-grid">
          <div
            v-for="invoice in invoices"
            :key="invoice.id"
            class="invoice-card"
          >
            <div class="invoice-header">
              <div class="invoice-number">#{{ invoice.invoice_no }}</div>
              <div class="invoice-status-wrapper">
                <b-badge :variant="getInvoiceStatusVariant(invoice.status)">
                  {{ invoice.status }}
                </b-badge>
              </div>
            </div>
            <div class="invoice-amount">
              {{ formatPrice(invoice.amount) }}
            </div>
            <div v-if="invoice.description" class="invoice-description">
              {{ invoice.description }}
            </div>
            <div class="invoice-footer">
              <div class="invoice-date">
                <feather-icon icon="CalendarIcon" size="14" />
                <span>{{ invoice.date }}</span>
              </div>
              <div class="invoice-type">
                <feather-icon icon="TagIcon" size="14" />
                <span>{{ getInvoiceTypeName(invoice.type) }}</span>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="no-invoices">
          <feather-icon icon="FileTextIcon" size="48" class="empty-icon" />
          <p>{{ $t("common.no-invoices-yet") }}</p>
        </div>
      </b-card-body>
    </b-card>

    <!-- Doctor Notes Section -->
    <b-card
      v-if="patientSurgeryData && patientSurgeryData.patient_card"
      no-body
      class="info-card mb-2"
    >
      <b-card-header class="section-header">
        <div class="d-flex justify-content-between align-items-center w-100">
          <h5 class="mb-0">
            <feather-icon icon="FileTextIcon" size="18" class="mr-2" />
            {{ $t("surgery.doctor-notes") }}
          </h5>
          <b-button
            variant="primary"
            size="sm"
            class="ml-3"
            @click="$bvModal.show('modal-add-doctor-note')"
          >
            <feather-icon icon="PlusIcon" size="14" class="mr-1" />
            {{ $t("surgery.add-doctor-note") }}
          </b-button>
        </div>
      </b-card-header>
      <b-card-body class="p-2">
        <div v-if="doctorNotes.length === 0" class="no-data-empty">
          <feather-icon icon="FileTextIcon" size="48" class="empty-icon" />
          <p>{{ $t("common.no-data-available") }}</p>
        </div>
        <div v-else class="modern-cards-grid">
          <div v-for="note in doctorNotes" :key="note.id" class="modern-card">
            <div class="modern-card-header">
              <div class="modern-card-icon">
                <feather-icon
                  icon="FileTextIcon"
                  size="24"
                  class="text-primary"
                />
              </div>
              <div class="modern-card-title">
                {{
                  note.created_by && note.created_by.name
                    ? note.created_by.name
                    : $t("surgery.doctor-note")
                }}
              </div>
            </div>
            <div class="modern-card-content">
              <div class="modern-card-note-text">{{ note.note }}</div>
              <div class="modern-card-info-item">
                <feather-icon icon="CalendarIcon" size="14" />
                <span>{{ note.date }}</span>
              </div>
            </div>
          </div>
        </div>
      </b-card-body>
    </b-card>

    <!-- Scanned Attachments Section (for CLINIC type only) -->
    <b-card
      v-if="
        patientSurgeryData &&
        patientSurgeryData.patient_card &&
        patientSurgeryData.patient_card.type == 'CLINIC'
      "
      class="mb-2"
      style="padding: 0"
    >
      <ScannedAttachments
        :patient-card-id="patientSurgeryData.patient_card.id"
      />
    </b-card>

    <!-- Patient Attachments Section -->
    <div
      v-if="patientSurgeryData && patientSurgeryData.patient_card"
      class="mb-2 pa-0"
    >
      <Attachments
        ref="attachments"
        :patient-card-id="patientSurgeryData.patient_card.id"
      />
    </div>

    <!-- Print Components -->
    <OperativeNotesPrint
      v-if="patientSurgeryData && patientSurgeryData.id"
      ref="operativeNotesPrint"
      :patientSurgeryData="patientSurgeryData"
    />
    <AgreementPrint
      v-if="patientSurgeryData && patientSurgeryData.id"
      ref="agreementPrint"
      :patientSurgeryData="patientSurgeryData"
      :surgeryName="agreementSurgeryName"
    />
    <PatientDischargeAgainstAdvicePrint
      v-if="patientSurgeryData && patientSurgeryData.id"
      ref="dischargeAgainstAdvicePrint"
      :patientSurgeryData="patientSurgeryData"
    />

    <b-modal
      id="modal-edit-doctor-surgery"
      :title="$t('accounting.edit-doctor-surgery')"
      ok-only
      centered
      size="lg"
      :no-close-on-backdrop="true"
    >
      <b-card-text>
        <b-row>
          <b-col md="12" cols="12" class="mt-3">
            <b-form-group label="Doctors">
              <v-select
                v-model="selectedDoctors"
                :options="doctors"
                multiple
                label="name"
                :reduce="(doctor) => doctor"
                :close-on-select="false"
              >
                <template #option="{ name, type }">
                  <b style="margin: 0">
                    {{ name }}
                    <span class="text-primary">
                      ({{
                        type == "surgeon"
                          ? $t("hospital.surgeon")
                          : $t("hospital.anesthetist")
                      }})
                    </span>
                  </b>
                </template>
                <template #selected-option="{ name, type }">
                  <b style="margin: 0">
                    {{ name }}
                    <span class="text-success">
                      ({{
                        type == "surgeon"
                          ? $t("hospital.surgeon")
                          : $t("hospital.anesthetist")
                      }})
                    </span>
                  </b>
                </template>
              </v-select>
            </b-form-group>
          </b-col>
          <!-- <b-col md="12" cols="12" class="mb-2">
            <div
              v-if="selectedDoctors.length > 0"
              class="d-flex justify-content-center flex-wrap"
            >
              <div
                v-for="doctor in selectedDoctors"
                :key="doctor.id"
                class="doctor-card"
              >
                <p class="mb-0">
                  {{ doctor.name }}
                </p>
                <div class="role-selection">
                  <b-form-checkbox
                    v-model="doctor.type"
                    :value="'surgeon'"
                    :unchecked-value="'anesthetist'"
                    class="role-checkbox"
                    switch
                    default-value="surgeon"
                  >
                    {{ doctor.type }}
                  </b-form-checkbox>
                </div>
              </div>
            </div>
          </b-col> -->
        </b-row>
      </b-card-text>
      <template #modal-footer="{}">
        <b-button
          variant="primary"
          @click="submitEditDoctorSurgery"
          class="m-0"
          :disabled="loadingEditDoctorSurgery"
          block
        >
          <span v-if="!loadingEditDoctorSurgery">
            {{ $t("common.save") }}
          </span>
          <b-spinner small v-else></b-spinner>
        </b-button>
      </template>
    </b-modal>
    <b-modal
      id="modal-edit-doctor-assistants"
      :title="$t('accounting.edit-doctor-assistants')"
      ok-only
      centered
      size="lg"
      :no-close-on-backdrop="true"
    >
      <b-card-text>
        <b-row>
          <b-col md="12" cols="12" class="mt-3">
            <b-form-group label="Doctors">
              <v-select
                v-model="selectedDoctorAssistants"
                :options="doctorAssistants"
                multiple
                :close-on-select="false"
              >
                <template #option="{ name, type }">
                  <b style="margin: 0">
                    {{ name }}
                    <span class="text-primary">
                      ({{
                        type == "surgeon_assistant"
                          ? $t("hospital.surgeon-assistant")
                          : $t("hospital.anesthetist-assistant")
                      }})
                    </span>
                  </b>
                </template>
                <template #selected-option="{ name, type }">
                  <b style="margin: 0">
                    {{ name }}
                    <span class="text-success">
                      ({{
                        type == "surgeon_assistant"
                          ? $t("hospital.surgeon-assistant")
                          : $t("hospital.anesthetist-assistant")
                      }})
                    </span>
                  </b>
                </template>
              </v-select>
            </b-form-group>
          </b-col>
        </b-row>
      </b-card-text>
      <template #modal-footer="{}">
        <b-button
          variant="primary"
          @click="submitEditDoctorAssistants"
          class="m-0"
          :disabled="loadingEditDoctorAssistants"
          block
        >
          <span v-if="!loadingEditDoctorAssistants">
            {{ $t("common.save") }}
          </span>
          <b-spinner small v-else></b-spinner>
        </b-button>
      </template>
    </b-modal>
    <b-modal
      id="modal-change-surgery"
      :title="$t('surgery.change-surgery')"
      ok-only
      centered
      size="lg"
      :no-close-on-backdrop="true"
    >
      <b-card-text>
        <b-row>
          <b-col md="12" cols="12" class="mt-3">
            <b-form-group :label="$t('surgery.surgery')">
              <v-select
                v-model="selectedSurgery"
                :options="availableSurgeriesOptions"
                :placeholder="$t('surgery.choose-surgery')"
                label="title"
                :reduce="(option) => option.id"
              />
            </b-form-group>
          </b-col>
        </b-row>
      </b-card-text>
      <template #modal-footer="{}">
        <b-button
          variant="primary"
          @click="submitChangeSurgery"
          class="m-0"
          :disabled="loadingChangeSurgery"
          block
        >
          <span v-if="!loadingChangeSurgery">{{ $t("common.save") }}</span>
          <b-spinner small v-else></b-spinner>
        </b-button>
      </template>
    </b-modal>
    <b-modal
      id="modal-agreement-print"
      :title="$t('reception.print-agreement')"
      ok-only
      centered
      size="lg"
      :no-close-on-backdrop="true"
    >
      <b-card-text>
        <b-row>
          <b-col md="12" cols="12" class="mt-3">
            <b-form-group :label="$t('surgery.surgery')">
              <v-select
                v-model="selectedAgreementSurgery"
                :options="availableSurgeriesOptions"
                :placeholder="$t('surgery.choose-surgery')"
                label="title"
                :reduce="(option) => option.title"
                @input="agreementSurgeryName = $event"
              />
            </b-form-group>
          </b-col>
          <b-col md="12" cols="12" class="mt-2">
            <b-form-group
              :label="
                $t('surgery.surgery') +
                ' (' +
                $t('common.or') +
                ' ' +
                $t('common.write') +
                ')'
              "
            >
              <b-form-input
                v-model="agreementSurgeryName"
                :placeholder="$t('surgery.surgery')"
              />
            </b-form-group>
          </b-col>
        </b-row>
      </b-card-text>
      <template #modal-footer="{}">
        <b-button
          variant="primary"
          @click="generateAgreement()"
          class="m-0"
          block
        >
          {{ $t("common.print") }}
        </b-button>
      </template>
    </b-modal>

    <!-- Add Lab Test Modal -->
    <b-modal
      id="modal-add-lab-test"
      :no-close-on-backdrop="true"
      ok-only
      centered
      size="lg"
      :title="$t('reception.add-lab-test')"
      :ok-disabled="!canSubmitLab"
      @ok="submitLab"
    >
      <b-card-text>
        <b-row>
          <b-col md="6" cols="12">
            <AutoComplete
              v-model="labCreate.singleTests"
              :label="$t('lab.test')"
              :placeholder="$t('lab.test')"
              :multiple="true"
              param-query="search"
              :serializer="labTestSerializer"
              endpoint="test"
              :close-on-select="false"
              text-title="name"
              @input="addTestsToList"
            />
          </b-col>
          <b-col md="6" cols="12">
            <AutoComplete
              v-model="labCreate.group"
              :label="$t('lab.test-group')"
              :placeholder="$t('lab.test-group')"
              param-query="search"
              :serializer="(val) => val.id"
              endpoint="group"
              text-title="name"
              @input="selectGroup"
            />
          </b-col>
          <b-col md="6">
            <b-form-group
              :label="$t('lab.sample-id')"
              label-for="sample-id"
              label-class="font-weight-bold"
            >
              <b-form-input
                id="sample-id"
                v-model="labCreate.sampleId"
                :placeholder="$t('lab.enter-sample-id')"
                trim
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group
              :label="$t('lab.sample-date')"
              label-for="sample-date"
              label-class="font-weight-bold"
            >
              <b-form-datepicker
                id="sample-date"
                v-model="labCreate.sampleDate"
                :max="new Date().toISOString().slice(0, 10)"
                :placeholder="$t('lab.select-date')"
                today-button
                reset-button
                close-button
                class="mb-2"
              />
            </b-form-group>
          </b-col>
        </b-row>
        <!-- Selected Tests Section -->
        <div class="selected-tests-section mt-3">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <h5 class="mb-0">{{ $t("lab.test") }}</h5>
            <b-button
              v-if="labTestListFiltered.length > 0"
              size="sm"
              variant="outline-danger"
              @click="clearAllTests"
            >
              <i class="fas fa-trash-alt mr-1" />
              {{ $t("lab.clear-all") }}
            </b-button>
          </div>
          <div class="selected-tests-container">
            <div v-if="!labTestListFiltered.length" class="text-center py-4">
              <i class="fas fa-vial text-muted mb-2" style="font-size: 2rem" />
              <p class="text-muted mb-0">
                {{ $t("lab.no-tests-selected-message") }}
              </p>
            </div>
            <b-table
              v-else
              :items="labTestListFiltered"
              :fields="labTestColumns"
              striped
              hover
              responsive
              show-empty
              :empty-text="$t('lab.no-tests-selected')"
              class="mb-0"
              thead-class="bg-light"
            >
              <template #cell(name)="data">
                <span class="font-weight-medium">{{ data.item.name }}</span>
              </template>
              <template #cell(price)="data">
                <b-badge variant="light" class="text-dark">
                  {{ formatPrice(data.item.price) }}
                </b-badge>
              </template>
            </b-table>
          </div>
          <!-- Summary Section -->
          <div
            v-if="labTestListFiltered.length > 0"
            class="d-flex justify-content-between mt-2 p-2 bg-light rounded"
          >
            <span class="font-weight-bold"
              >{{ $t("lab.total-tests") }}:
              {{ labTestListFiltered.length }}</span
            >
            <span class="font-weight-bold"
              >{{ $t("lab.total-price") }}:
              {{ formatPrice(labTotalPrice) }}</span
            >
          </div>
        </div>
      </b-card-text>
    </b-modal>

    <!-- Add Drug Modal -->
    <b-modal
      id="modal-add-drug"
      :no-close-on-backdrop="true"
      ok-only
      centered
      size="lg"
      :title="$t('reception.add-drug')"
      :ok-disabled="!canSubmitDrug"
      @ok="submitDrug"
    >
      <b-card-text>
        <b-row>
          <b-col md="6">
            <b-form-group :label="$t('office.source_of_drug')">
              <v-select
                v-model="drugCreate.source_of_drug"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="[
                  { label: 'Hospital', value: 'hospital' },
                  { label: 'Pharmacy', value: 'pharmacy' },
                  { label: 'External', value: 'external' },
                ]"
                :reduce="(option) => option.value"
                :placeholder="$t('office.source_of_drug')"
                @input="handleDrugSourceChange"
              />
            </b-form-group>
          </b-col>
          <b-col
            v-if="
              drugCreate.source_of_drug !== 'external' &&
              drugCreate.source_of_drug === 'pharmacy'
            "
            md="6"
          >
            <AutoComplete
              v-model="drugCreate.drug_scientific_name_id"
              :label="$t('common.drug_scientific_name')"
              :placeholder="$t('common.drug_scientific_name')"
              :serializer="(val) => val.id"
              endpoint="drugScientificName"
              text-title="name"
            />
            <AutoComplete
              v-model="drugCreate.item_id"
              :disabled="!drugCreate.source_of_drug"
              :label="$t('office.item_name')"
              :placeholder="$t('office.item_name')"
              :serializer="(val) => val.id"
              endpoint="item"
              text-title="name"
              :other-query="`filter[type]=${drugCreate.source_of_drug}&filter[drug_scientific_name_id]=${drugCreate.drug_scientific_name_id}`"
            />
          </b-col>
          <b-col
            v-if="
              drugCreate.source_of_drug !== 'external' &&
              drugCreate.source_of_drug === 'hospital'
            "
            md="6"
          >
            <AutoComplete
              v-model="drugCreate.item_id"
              :disabled="!drugCreate.source_of_drug"
              :label="$t('office.item_name')"
              :placeholder="$t('office.item_name')"
              :serializer="(val) => val.id"
              endpoint="item"
              text-title="name"
              :other-query="`filter[type]=${drugCreate.source_of_drug}`"
            />
          </b-col>
          <b-col v-if="drugCreate.source_of_drug === 'external'" md="6">
            <b-form-group :label="$t('office.item_name_external')">
              <b-form-input
                v-model="drugCreate.item_name"
                :placeholder="$t('office.item_name_external')"
              />
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col md="6">
            <b-form-group :label="$t('office.number_of_doses')">
              <b-form-input
                v-model="drugCreate.number_of_doses"
                type="number"
                :placeholder="$t('office.number_of_doses')"
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group :label="$t('office.duration_between_doses')">
              <b-form-input
                v-model="drugCreate.duration_between_doses"
                :placeholder="$t('office.duration_between_doses')"
              />
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col md="6">
            <b-form-group :label="$t('office.type')">
              <b-form-input
                v-model="drugCreate.type"
                :placeholder="$t('office.type')"
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group :label="$t('office.dose')">
              <b-form-input
                v-model="drugCreate.dose"
                :placeholder="$t('office.dose')"
              />
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col md="12">
            <b-form-group :label="$t('office.start_time')">
              <datetime
                v-model="drugCreate.start_time"
                input-class="form-control"
                type="datetime"
              />
            </b-form-group>
          </b-col>
          <b-col md="12">
            <b-form-group :label="$t('office.description')">
              <b-form-textarea
                v-model="drugCreate.description"
                :placeholder="$t('office.description')"
                rows="3"
              />
            </b-form-group>
          </b-col>
        </b-row>
      </b-card-text>
    </b-modal>

    <!-- Add Radiology Modal -->
    <b-modal
      id="modal-add-radiology"
      :no-close-on-backdrop="true"
      ok-only
      centered
      size="lg"
      :title="$t('reception.add-radiology')"
      :ok-disabled="!canSubmitRadiology"
      @ok="submitRadiology"
    >
      <b-card-text>
        <b-row>
          <b-col md="6">
            <AutoComplete
              v-model="radiologyCreate.radiology_id"
              :label="$t('radiology.radiology')"
              :placeholder="$t('radiology.radiology')"
              param-query="search"
              :serializer="(val) => val.id"
              endpoint="radiology"
              text-title="name"
            />
          </b-col>
          <b-col md="6">
            <b-form-group :label="$t('common.url')" label-for="url">
              <b-form-input
                id="url"
                v-model="radiologyCreate.url"
                :placeholder="$t('common.url')"
              />
            </b-form-group>
          </b-col>
        </b-row>
      </b-card-text>
    </b-modal>

    <!-- Add Blood Transfusion Modal -->
    <b-modal
      id="modal-add-blood-transfusion"
      :no-close-on-backdrop="true"
      ok-only
      centered
      size="lg"
      :title="$t('bloodBank.add-blood-transfusion')"
    >
      <b-card-text>
        <b-row>
          <b-col md="12" style="font-weight: bold">
            <AutoComplete
              v-model="bloodTransfusionForm.blood_transfusion_type_id"
              :label="$t('bloodBank.blood-transfusion-type')"
              :placeholder="$t('bloodBank.blood-transfusion-type')"
              param-query="name"
              :serializer="(val) => val.id"
              endpoint="bloodTransfusionTypes"
              text-title="name"
              keyword="name"
            />
            <p class="text-danger mt-1">
              {{ errorForm.blood_transfusion_type_id }}
            </p>
          </b-col>
        </b-row>
        <b-row>
          <b-col md="4" style="font-weight: bold">
            <b-form-group
              :label="$t('bloodBank.patient-abo')"
              label-for="patient_abo"
            >
              <v-select
                v-model="bloodTransfusionForm.patient_abo"
                :options="aboOptions"
                placeholder="A, B, AB, O"
              />
              <p class="text-danger mt-1">{{ errorForm.patient_abo }}</p>
            </b-form-group>
          </b-col>
          <b-col md="4" style="font-weight: bold">
            <b-form-group
              :label="$t('bloodBank.patient-rh')"
              label-for="patient_rh"
            >
              <v-select
                v-model="bloodTransfusionForm.patient_rh"
                :options="rhOptions"
                placeholder="Positive, Negative"
              />
              <p class="text-danger mt-1">{{ errorForm.patient_rh }}</p>
            </b-form-group>
          </b-col>
          <b-col md="4" style="font-weight: bold">
            <b-form-group
              :label="$t('bloodBank.number-of-units')"
              label-for="number_of_units"
            >
              <b-form-input
                type="number"
                v-model="bloodTransfusionForm.number_of_units"
                :placeholder="$t('bloodBank.number-of-units')"
              />
              <p class="text-danger mt-1">{{ errorForm.number_of_units }}</p>
            </b-form-group>
          </b-col>
        </b-row>
      </b-card-text>
      <template #modal-footer="{}">
        <b-button
          variant="primary"
          @click="submitBloodTransfusion"
          class="m-0"
          :disabled="loadingBloodTransfusion"
          block
        >
          <span v-if="!loadingBloodTransfusion">{{ $t("common.add") }}</span>
          <b-spinner small v-else></b-spinner>
        </b-button>
      </template>
    </b-modal>

    <!-- Add Doctor Note Modal -->
    <b-modal
      id="modal-add-doctor-note"
      :no-close-on-backdrop="true"
      ok-only
      centered
      size="lg"
      :title="$t('surgery.add-doctor-note')"
    >
      <b-card-text>
        <b-row>
          <b-col md="12">
            <b-form-group :label="$t('common.date')" label-for="date">
              <b-form-datepicker
                id="date"
                v-model="doctorNoteForm.date"
                :placeholder="$t('common.date')"
                today-button
                reset-button
                close-button
                class="mb-2"
              />
            </b-form-group>
          </b-col>
          <b-col md="12">
            <b-form-group :label="$t('common.note')" label-for="note">
              <b-form-textarea
                id="note"
                v-model="doctorNoteForm.note"
                :placeholder="$t('common.note')"
                rows="5"
              />
            </b-form-group>
          </b-col>
        </b-row>
      </b-card-text>
      <template #modal-footer="{}">
        <b-button
          variant="primary"
          @click="submitDoctorNote"
          class="m-0"
          :disabled="loadingDoctorNote"
          block
        >
          <span v-if="!loadingDoctorNote">{{ $t("common.add") }}</span>
          <b-spinner small v-else></b-spinner>
        </b-button>
      </template>
    </b-modal>
  </div>
</template>
<script>
import {
  BCard,
  BRow,
  BCol,
  BButton,
  BLink,
  BCardText,
  BCardHeader,
  BCardBody,
  BFormGroup,
  BFormTextarea,
  BFormInput,
  BTable,
  BSpinner,
  BFormCheckbox,
  BBadge,
  BDropdown,
  BDropdownItem,
  BFormDatepicker,
} from "bootstrap-vue";

import ReceptionHistoryCard from "@/components/reception/ReceptionHistoryCard";
import ReceptionPatientCardWithQR from "@/components/reception/ReceptionPatientCardWithQR";
import vSelect from "vue-select";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import PatientRoom from "@/views/rooms/PatientRoom.vue";
import DoctorAssist from "@/views/surgery/DoctorAssist.vue";
import PatientSurgeryStaff from "@/views/surgery/PatientSurgeryStaff.vue";
import PatientSurgeryInvoice from "@/views/surgery/SurgeryInvoice.vue";
import Datepicker from "vuejs-datepicker";
import { async } from "vue-phone-number-input/dist/vue-phone-number-input.common";
import ActionsButtons from "@/components/common/ActionsButtons.vue";
import DeleteModal from "@/components/modals/DeleteModal.vue";
const SurgeryInvoice = () => import("@/views/surgery/SurgeryInvoice.vue");

import PatientSurgeyConsumables from "@/views/surgery/PatientSurgeyConsumables.vue";
import Attachments from "@/views/Reception/Attachments.vue";
import ScannedAttachments from "@/views/Clinic/ScannedAttachments.vue";
import OperativeNotesPrint from "@/components/printables/OperativeNotesPrint.vue";
import AgreementPrint from "@/components/printables/AgreementPrint.vue";
import PatientDischargeAgainstAdvicePrint from "@/components/printables/PatientDischargeAgainstAdvicePrint.vue";
import PateintCardTest from "@/views/lab/PatientCardTest.vue";
import PatientDrug from "@/views/pharmacy/PatientDrug.vue";
import PatientCardRadiology from "@/views/radiology/PatientCardRadiology.vue";
import { Datetime } from "vue-datetime";
import "vue-datetime/dist/vue-datetime.css";
const AutoComplete = () => import("@/components/common/AutoComplete.vue");

export default {
  components: {
    Datepicker,
    BRow,
    BTable,
    BCol,
    BLink,
    BFormInput,
    BFormGroup,
    BCard,
    BCardText,
    BButton,
    ReceptionHistoryCard,
    ReceptionPatientCardWithQR,
    AutoComplete,
    vSelect,
    ToastificationContent,
    PatientRoom,
    DoctorAssist,
    PatientSurgeryStaff,
    ActionsButtons,
    PatientSurgeryInvoice,
    DeleteModal,
    BFormTextarea,
    SurgeryInvoice,
    PatientSurgeyConsumables,
    Attachments,
    ScannedAttachments,
    BSpinner,
    BFormCheckbox,
    BCardHeader,
    BCardBody,
    BBadge,
    BDropdown,
    BDropdownItem,
    BFormDatepicker,
    OperativeNotesPrint,
    AgreementPrint,
    PatientDischargeAgainstAdvicePrint,
    PateintCardTest,
    PatientDrug,
    PatientCardRadiology,
    Datetime,
    AutoComplete,
  },
  data() {
    return {
      params: {
        form: {},
        path: "",
      },
      EditForm: {
        patient_card_id: this.$route.params.id,
        doctor_surgery_id: "",
        // price: this.finalPrice,
        price: "",
        surgery_date_time: "",

        surgery_discount: 0,
        doctor_discount: 0,
        _method: "POST",
        sub_surgery_id: this.patientSurgeryData?.doctor_surgery.surgery_id,
      },
      updateSurgeryData: {
        surgery_date_time: "",
        patient_case: "",
        patient_case_details: "",
        diagnosis: "",
        procedure: "",
        final_result: "",
        _method: "PUT",
      },

      patientSurgeryData: {},
      patientTest: {},
      patientTestForm: {
        patient_card_id: "",
        tests: [],
        _method: "PUT",
      },
      errorForm: {
        patient_card_id: "",
        doctor_surgery_id: "",
        price: "",
        surgery_date_time: "",
        surgery_discount: 0,
        doctor_discount: 0,
      },
      take: 10,
      loading: true,
      typeList: [
        {
          text: "Type 1",
          value: "0",
        },
        {
          text: "Type 2",
          value: "1",
        },
      ],
      staffCreateMode: "",
      staffForm: {
        patient_card_doctor_surgery_id: "",
        employee_id: "",
        anesthetist_type: "",
        price: "",
        _method: "PUT",
      },
      patientRadiology: {},
      patientRadiologyForm: {
        type: "",
        url: "",
        patient_card_id: "",
        radiology_id: "",
        _method: "PUT",
      },
      surgeryId: null,
      surgery: {},
      doctorSurgery: null,
      tableColumns: [
        {
          key: "name",
          label: this.$t("common.Full Name"),
          sortable: true,
        },
        {
          key: "price",
          label: this.$t("common.price"),
          sortable: true,
        },
        { key: "Actions", label: this.$t("common.actions") },
      ],
      staffTableColumns: [
        {
          key: "employee_id",
          label: this.$t("common.Full Name"),
          sortable: true,
        },
        {
          key: "anesthetist_type",
          label: this.$t("common.type"),
          sortable: true,
        },
        {
          key: "price",
          label: this.$t("common.price"),
          sortable: true,
        },
        { key: "Actions", label: this.$t("common.actions") },
      ],
      doctor_surgery_fields: [
        { key: "doctor.name", label: this.$t("surgery.doctor-name") },
        { key: "type", label: this.$t("surgery.doctor-type") },
        { key: "doctor.phone", label: this.$t("surgery.phone") },
        { key: "created_at", label: this.$t("surgery.created-at") },
      ],
      doctor_assist_fields: [
        { key: "name", label: this.$t("surgery.doctor-name") },
        { key: "type", label: this.$t("surgery.doctor-type") },
        { key: "phone", label: this.$t("surgery.phone") },
        { key: "created_at", label: this.$t("surgery.created-at") },
      ],
      updateSurgeryInfoIndicator: false,
      updateSubSurgeryInfoIndicator: false,
      doctorAssist: {
        patient_card_doctor_surgery_id: "",
        name: "",
        price: "",
        _method: "PUT",
      },
      updateDoctorSurgery: null,
      doctorSurgeryData: [],
      loadingEditDoctorSurgery: false,
      loadingOpenEditDoctorSurgeryModal: false,
      doctors: [],
      selectedDoctors: [],
      final_result: null,
      final_result_options: [
        { label: this.$t("common.success-operation"), value: "success" },
        { label: this.$t("common.failed-operation"), value: "failed" },
      ],
      doctorAssistants: [],
      selectedDoctorAssistants: [],
      selectedSurgery: null,
      availableSurgeriesOptions: [],
      loadingChangeSurgery: false,
      tempSurgery: null,
      surgeryData: null,
      agreementSurgeryName: "",
      showAgreementPrintModal: false,
      selectedAgreementSurgery: null,
      // Lab Test Data
      labCreate: {
        singleTests: [],
        group: null,
        sampleId: "",
        sampleDate: new Date().toISOString().slice(0, 10),
        lastSelectedGroupId: null,
        testsList: [],
      },
      labTestColumns: [
        { key: "name", label: this.$t("lab.test-name") },
        { key: "price", label: this.$t("common.price") },
      ],
      // Drug Data
      drugCreate: {
        item_id: null,
        item_name: "",
        number_of_doses: "",
        duration_between_doses: "",
        source_of_drug: "",
        type: "",
        dose: "",
        start_time: new Date().toISOString(),
        description: "",
        drug_scientific_name_id: null,
      },
      // Radiology Data
      radiologyCreate: { radiology_id: null, type: "1", url: "" },
      // Blood Transfusion Data
      bloodTransfusionForm: {
        patient_card_id: "",
        patient_abo: "",
        patient_rh: "",
        blood_transfusion_type_id: "",
        number_of_units: "",
        _method: "POST",
      },
      aboOptions: ["A", "B", "AB", "O"],
      rhOptions: ["Positive", "Negative"],
      loadingBloodTransfusion: false,
      bloodTransfusions: [],
      transfusionTypes: [],
      // Invoices Data
      invoices: [],
      // Patient Tests Data
      patientTests: [],
      // Patient Drugs Data
      patientDrugs: [],
      // Patient Radiology Data
      patientRadiology: [],
      // Doctor Notes Data
      doctorNoteForm: {
        date: new Date().toISOString().slice(0, 10),
        note: "",
        patient_id: "",
        patient_card_surgery_id: "",
        _method: "POST",
      },
      loadingDoctorNote: false,
      doctorNotes: [],
      surgeryDoctorsOptions: [],
    };
  },
  mounted() {
    this.getPatientSurgeryInfo();
    this.getPatientCard();
    this.getTransfusionTypeList();
  },
  watch: {
    "labCreate.singleTests": {
      handler(newVal, oldVal) {
        if (!oldVal) return;
        if (newVal.length < oldVal.length) {
          const newIds = newVal.map((item) => item.test_id || item.id);
          const removedItems = oldVal.filter(
            (item) => !newIds.includes(item.test_id || item.id)
          );
          removedItems.forEach((removedItem) => {
            this.labCreate.testsList = this.labCreate.testsList.filter(
              (test) => test.test_id !== (removedItem.test_id || removedItem.id)
            );
          });
        }
      },
      deep: true,
    },
    surgeryId(newSurgeryId) {
      // Check if surgeryId is not null or undefined
      if (newSurgeryId != null) {
        // Call the method to get surgery details
        this.getSurgeryList();
      } else {
        this.updateSurgeryData.price = "";
      }

      if (newSurgeryId != this.patientSurgeryData.doctor_surgery.surgery_id) {
        this.doctorSurgery = "";
      }
    },
    doctorSurgery() {
      // this.EditForm.price = this.EditForm.price + this.doctorSurgery.price;
    },
  },
  computed: {
    finalPrice() {
      return this.surgery.price + this.doctorSurgery.price;
    },
    labTestListFiltered() {
      const uniqueMap = new Map();
      this.labCreate.testsList.forEach((test) => {
        const id = test.test_id || test.id;
        if (id) {
          uniqueMap.set(id, test);
        }
      });
      return Array.from(uniqueMap.values());
    },
    labTotalPrice() {
      return this.labTestListFiltered.reduce(
        (sum, test) => sum + Number(test.price || 0),
        0
      );
    },
    canSubmitLab() {
      return this.labTestListFiltered.length > 0;
    },
    canSubmitDrug() {
      if (!this.drugCreate.source_of_drug) return false;

      // For external drugs, only item_name is required
      if (this.drugCreate.source_of_drug === "external") {
        return !!this.drugCreate.item_name;
      }

      // For hospital/pharmacy drugs, item_id, number_of_doses, and duration_between_doses are required
      return (
        this.drugCreate.item_id &&
        this.drugCreate.number_of_doses &&
        this.drugCreate.duration_between_doses
      );
    },
    canSubmitRadiology() {
      return this.radiologyCreate.radiology_id;
    },
  },
  methods: {
    async getDoctorAssistants() {
      try {
        const response = await this.getList("/doctorAssistants");
        this.doctorAssistants = response.data.data;
      } catch (error) {
        console.error("Error fetching doctor assistants:", error);
      }
    },
    async getDoctors() {
      await this.getList("doctor?take=100").then((response) => {
        this.doctors = response.data.data.map((doctor) => ({
          id: doctor.id,
          name: doctor.name,
          type: doctor.type,
        }));
      });
    },
    // edit doctor surgery
    async openEditDoctorSurgeryModal(item) {
      this.loadingOpenEditDoctorSurgeryModal = true;
      this.tempSurgery = this.patientSurgeryData;
      console.log("tempSurgery", this.tempSurgery);
      item = this.patientSurgeryData;
      this.selectedDoctors = item.doctors_prices.items.map((doctor) => ({
        id: doctor.doctor_id,
        name: doctor.doctor.name,
        type: doctor.type,
      }));

      await this.getDoctors();
      this.loadingOpenEditDoctorSurgeryModal = false;
      this.$bvModal.show(`modal-edit-doctor-surgery`);
    },
    // edit doctor assistants
    async openEditDoctorAssistantsModal(item) {
      this.loadingOpenEditDoctorAssistantsModal = true;
      this.tempSurgery = this.patientSurgeryData;

      item = this.patientSurgeryData;
      this.selectedDoctorAssistants = item.doctor_assist.map((doctor) => ({
        id: doctor.id,
        name: doctor.name,
        type: doctor.type,
      }));

      await this.getDoctorAssistants();
      this.loadingOpenEditDoctorAssistantsModal = false;
      this.$bvModal.show(`modal-edit-doctor-assistants`);
    },

    async submitEditDoctorSurgery() {
      console.log("surgeryPrice", this.doctorSurgeryData);
      this.loadingEditDoctorSurgery = true;

      this.params.form = {
        surgery_id: this.tempSurgery.surgery_id,
        doctors: this.selectedDoctors.map((doctor) => ({
          id: doctor.id,
          type: doctor.type,
        })),
        _method: "PUT",
      };

      // Use the id of the current surgery record for the endpoint
      this.params.path = `/patient-surgery/${this.$route.params.id}`;

      try {
        await this.$store.dispatch("put", this.params);
        this.loadingEditDoctorSurgery = false;
        this.$bvModal.hide("modal-edit-doctor-surgery");
        this.getPatientSurgeryInfo();
        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: {
            title: "Success",
            variant: "success",
            text: "Doctor surgery updated successfully",
          },
        });
      } catch (error) {
        this.loadingEditDoctorSurgery = false;
        if (error.response && error.response.status === 400) {
          Object.keys(error.response.data).forEach((item) => {
            this.$toast({
              component: ToastificationContent,
              position: "top-right",
              props: {
                title: "Validation Error",
                variant: "danger",
                text: error.response.data[item][0],
              },
            });
          });
        }
      }
    },
    async submitEditDoctorAssistants() {
      console.log("doctorAssistants", this.selectedDoctorAssistants);
      this.loadingEditDoctorAssistants = true;

      this.params.form = {
        surgery_id: this.tempSurgery.surgery_id,
        doctor_assist: this.selectedDoctorAssistants.map((doctor) => ({
          id: doctor.id,
          type: doctor.type,
        })),
        _method: "PUT",
      };

      // Use the id of the current surgery record for the endpoint
      this.params.path = `/patient-surgery/${this.$route.params.id}`;

      try {
        await this.$store.dispatch("put", this.params);
        this.loadingEditDoctorAssistants = false;
        this.$bvModal.hide("modal-edit-doctor-assistants");
        this.getPatientSurgeryInfo();
        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: {
            title: "Success",
            variant: "success",
            text: "Doctor assistants updated successfully",
          },
        });
      } catch (error) {
        this.loadingEditDoctorAssistants = false;
        if (error.response && error.response.status === 400) {
          Object.keys(error.response.data).forEach((item) => {
            this.$toast({
              component: ToastificationContent,
              position: "top-right",
              props: {
                title: "Validation Error",
                variant: "danger",
                text: error.response.data[item][0],
              },
            });
          });
        }
      }
    },
    navigateWithDoctors(isSub) {
      this.$store.dispatch("updateDoctors", this.patientSurgeryData.doctors);

      // Navigate to the new route
      this.$router.push({
        name: "add-patient-surgery",
        params: {
          id: this.patientSurgeryData.patient_card.id,
          isSub: true,
        },
        query: {
          patientId: this.patientSurgeryData.patient_card.patient.id,
          isSub: true,
          surgeryId: this.patientSurgeryData.id,
        },
      });
    },
    navigateToEditSurgery() {
      if (!this.patientSurgeryData || !this.patientSurgeryData.patient_card) {
        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: {
            title: "Error",
            variant: "danger",
            text: "Patient card data is missing",
          },
        });
        return;
      }

      // Navigate to edit surgery page
      this.$router.push({
        name: "add-patient-surgery",
        params: {
          id: this.patientSurgeryData.patient_card.id,
        },
        query: {
          patientId:
            this.patientSurgeryData.patient_card.patient?.id ||
            this.patientSurgeryData.patient_card.patient_id,
          editSurgeryId: this.patientSurgeryData.id,
        },
      });
    },
    deleteModel(item, mode) {
      if (mode === "doctor_assist") {
        this.deleteForm = item;
        this.$bvModal.show("modal-doctor-assist-delete");
      } else {
        this.deleteForm = item;
        this.$bvModal.show("modal-staff-delete");
      }
    },
    printOperativeNotes() {
      if (this.$refs.operativeNotesPrint) {
        this.$refs.operativeNotesPrint.print();
      }
    },
    show(item, mode) {
      if (mode === "doctor-assist") {
        if (mode === true) {
          this.doctorAssist.id = item.id;
          this.doctorAssist.patient_card_doctor_surgery_id =
            this.doctorSurgeryId;
          this.doctorAssist.name = item.name;
          this.doctorAssist.price = item.price;
          this.doctorAssist._method = "PUT";

          this.$bvModal.show("modal-doctor-assist-edit");
        }
      } else {
        if (mode === false) {
          this.staffCreateMode = false;

          this.staffForm.id = item.id;
          this.staffForm.patient_card_doctor_surgery_id = this.doctorSurgeryId;
          this.staffForm.employee_id = item.employee_id;
          this.staffForm.price = item.price;
          this.staffForm.anesthetist_type = item.anesthetist_type;
          this.staffForm._method = "PUT";
          this.$bvModal.show("modal-staff-edit");
        } else {
          this.surgeryId = null;
          this.staffCreateMode = true;
          this.staffForm = {
            patient_card_doctor_surgery_id: this.doctorSurgeryId,
            employee_id: "",
            anesthetist_type: "",
            price: "",
            _method: "POST",
          };
        }
      }
    },

    async getSurgeryList() {
      this.loading = true;
      // check if there are any null value in the obj we sending

      let url = `/surgery?filter[id]=${this.surgeryId}`;

      await this.getList(url);
      this.surgery = this.all_data.data[0];
      if (this.surgery) {
        this.updateSurgeryData.price = this.surgery.price;
      }

      this.loading = false;
    },
    updateSurgeryInfo() {
      if (!this.updateSurgeryInfoIndicator) {
        this.updateSurgeryInfoIndicator = true;
        this.surgeryId = this.patientSurgeryData.doctor_surgery.surgery_id;
        this.doctorSurgery = this.patientSurgeryData.doctor_surgery.id;
        this.updateSurgeryData.price = this.patientSurgeryData.price;
        this.updateSurgeryData.patient_card_id =
          this.patientSurgeryData.patient_card_id;
        this.updateSurgeryData.surgery_date_time = this.formatDate(
          this.patientSurgeryData.surgery_date_time
        );
        this.updateSurgeryData.surgery_discount =
          this.patientSurgeryData.surgery_discount;
        this.updateSurgeryData.doctor_discount =
          this.patientSurgeryData.doctor_discount;
        this.updateSurgeryData.final_result =
          this.patientSurgeryData.final_result;
      } else {
        this.updateSurgeryData.surgery_date_time = this.formatDate(
          this.updateSurgeryData.surgery_date_time
        );

        this.updateSurgeryData.patient_card_id =
          this.patientSurgeryData.patient_card_id;
        this.updateSurgeryData.surgery_id = this.patientSurgeryData.surgery_id;
        console.log("updateSurgeryData", this.patientSurgeryData);
        this.params.path = `/patient-surgery/${this.$route.params.id}`;
        this.params.form = this.updateSurgeryData;
        this.$store
          .dispatch("update", this.params)
          .then(async (response) => {
            this.updateSurgeryInfoIndicator = false;
            this.getPatientSurgeryInfo();
            this.$toast({
              component: ToastificationContent,
              position: "top-right",
              props: {
                title: "Card Stopped",
                variant: "success",
                text: error.response.data[item][0],
              },
            });
            await this.getPatientSurgeryInfo();
            this.updateSurgeryInfoIndicator = false;
          })
          .catch((error) => {
            if (error.response.status === 400) {
              for (let item in error.response.data) {
                this.$toast({
                  component: ToastificationContent,
                  position: "top-right",
                  props: {
                    title: "validation error",
                    variant: "danger",
                    text: error.response.data[item][0],
                  },
                });
              }
            }
          });
      }
    },
    updateSubSurgeryInfo() {
      console.log(this.patientSurgeryData);

      if (!this.updateSubSurgeryInfoIndicator) {
        this.updateSubSurgeryInfoIndicator = true;
        this.surgeryId =
          this.patientSurgeryData.sub_surgeries[0].doctor_surgery.surgery_id;
        this.doctorSurgery =
          this.patientSurgeryData.sub_surgeries[0].doctor_surgery.id;
        this.updateSurgeryData.price =
          this.patientSurgeryData.sub_surgeries[0].price;
        this.updateSurgeryData.patient_card_id =
          this.patientSurgeryData.patient_card_id;
        this.updateSurgeryData.surgery_date_time = this.formatDate(
          this.patientSurgeryData.sub_surgeries[0].surgery_date_time
        );
        this.updateSurgeryData.surgery_discount =
          this.patientSurgeryData.sub_surgeries[0].surgery_discount;
        this.updateSurgeryData.doctor_discount =
          this.patientSurgeryData.sub_surgeries[0].doctor_discount;
      } else {
        this.updateSurgeryData.surgery_date_time = this.formatDate(
          this.patientSurgeryData.sub_surgeries[0].surgery_date_time
        );
        this.updateSurgeryData.patient_card_id =
          this.patientSurgeryData.patient_card_id;
        this.updateSurgeryData.doctor_surgery_id = this.doctorSurgery;
        this.updateSurgeryData.surgeryId = this.surgeryId;
        this.params.path = `/patient-surgery/${this.patientSurgeryData.sub_surgeries[0].id}`;
        this.params.form = this.updateSurgeryData;
        this.$store
          .dispatch("update", this.params)
          .then(async (response) => {
            this.updateSubSurgeryInfoIndicator = false;

            this.$toast({
              component: ToastificationContent,
              position: "top-right",
              props: {
                title: "Card Stopped",
                variant: "success",
                text: error.response.data[item][0],
              },
            });
            await this.getPatientSurgeryInfo();
            this.updateSurgeryInfoIndicator = false;
          })
          .catch((error) => {
            console.log(error);
            if (error.response.status === 400) {
              for (let item in error.response.data) {
                this.$toast({
                  component: ToastificationContent,
                  position: "top-right",
                  props: {
                    title: "validation error",
                    variant: "danger",
                    text: error.response.data[item][0],
                  },
                });
              }
            }
          });
      }
    },
    formatDate(date) {
      const newDate = new Date(date);
      const year = newDate.getFullYear();
      const month = String(newDate.getMonth() + 1).padStart(2, "0");
      const day = String(newDate.getDate()).padStart(2, "0");
      const hours = String(newDate.getHours()).padStart(2, "0");
      const minutes = String(newDate.getMinutes()).padStart(2, "0");

      return `${year}-${month}-${day} ${hours}:${minutes}`;
    },
    submitItem() {
      for (let item in this.errorForm) {
        this.errorForm[item] = "";
        if (this.EditForm[item] == "" || this.EditForm[item] == null)
          delete this.EditForm[item];
      }

      this.EditForm.surgery_date_time = this.formatDate(
        this.EditForm.surgery_date_time
      );

      // this.EditForm.price = this.EditForm.price;
      this.EditForm.patient_card_id = this.patientSurgeryData.patient_card.id;
      if (this.createMode) {
        (this.params.form = this.EditForm),
          (this.params.path = `/patient-surgery`),
          this.$store
            .dispatch("create", this.params)
            .then((response) => {
              this.getPatientSurgeryInfo();
            })
            .catch((error) => {
              if (error.response.status === 400) {
                for (let item in error.response.data) {
                  this.$toast({
                    component: ToastificationContent,
                    position: "top-right",
                    props: {
                      title: "validation error",
                      variant: "danger",
                      text: error.response.data[item][0],
                    },
                  });
                }
              }
            });
      } else {
        (this.params.form = this.EditForm),
          (this.params.path = `/patient-surgery/${this.EditForm.id}`),
          this.$store
            .dispatch("update", this.params)
            .then((response) => {
              this.getPatientsSurgeries();
            })
            .catch((error) => {
              if (error.response.status === 400) {
                for (let item in error.response.data) {
                  this.$toast({
                    component: ToastificationContent,
                    position: "top-right",
                    props: {
                      title: "validation error",
                      variant: "danger",
                      text: error.response.data[item][0],
                    },
                  });
                }
              }
            });
      }
    },

    async getPatientCard() {
      this.loading = true;
      await this.getPatient(
        `patientCard?filter[id]=${this.$route.params.id}&include=patient`
      );
      this.loading = false;
      for (let item in this.all_data) {
        if (this.all_data[item] == null) this.all_data[item] = "";
      }
      this.patientCardData = this.all_data.data[0];
    },
    async getPatientsSurgeries() {
      this.loading = true;
      // check if there are any null value in the obj we sending

      let url = `/patient-surgery?filter[patient_card_id]=${this.patientCardId}&page=${this.page}&take=${this.take}&sort=-id&include=doctorSurgery,patientCard`;

      if (this.doctorSurgeryFilter) {
        url += `&filter[doctor_surgery_id]=${this.doctorSurgeryFilter}`;
      }
      if (this.PatientCardFilter) {
        url += `&filter[patient_card_id]=${this.PatientCardFilter}`;
      }
      if (this.priceFilter) {
        url += `&filter[price]=${this.priceFilter}`;
      }

      await this.getList(url);
      this.dataCount = this.all_data.total;
      this.surgeryData = this.all_data;
      console.log("surgeryData", this.surgeryData);
      this.loading = false;
    },
    show(item, mode) {
      if (mode === "create") {
        this.surgeryId = null;
        this.createMode = true;
        this.EditForm = {
          patient_card_id: this.patientCardId,
          doctor_surgery_id: "",
          price: "",
          surgery_date_time: "",
          surgery_discount: "",
          doctor_discount: "",
          _method: "POST",
        };
      } else {
        this.createMode = false;
        this.EditForm.id = item.id;
        this.EditForm.patient_card_id = this.patientCardId;
        this.EditForm.doctor_surgery_id = item.doctor_surgery_id;
        // this.EditForm.price = item.price;
        this.EditForm.surgery_date_time = item.surgery_date_time;
        this.EditForm.surgery_discount = item.surgery_discount;
        this.EditForm.doctor_discount = item.doctor_discount;
        this.EditForm.surgery_date_time = item.surgery_date_time;
        this.EditForm._method = "PUT";
      }
      this.$bvModal.show("modal-surgery-edit");
    },

    async openAgreementPrintModal() {
      // Load available surgeries for selection
      if (this.availableSurgeriesOptions.length === 0) {
        await this.getList("surgery?take=100");
        this.availableSurgeriesOptions = this.all_data.data.map((surgery) => ({
          id: surgery.id,
          title: surgery.title,
        }));
      }

      // Set default surgery name from patientSurgeryData if available
      if (
        this.patientSurgeryData &&
        this.patientSurgeryData.surgery &&
        this.patientSurgeryData.surgery.title
      ) {
        this.agreementSurgeryName = this.patientSurgeryData.surgery.title;
        // Find and set the selected surgery in the dropdown
        const defaultSurgery = this.availableSurgeriesOptions.find(
          (s) => s.title === this.patientSurgeryData.surgery.title
        );
        this.selectedAgreementSurgery = defaultSurgery
          ? defaultSurgery.title
          : null;
      } else {
        this.agreementSurgeryName = "";
        this.selectedAgreementSurgery = null;
      }

      this.$bvModal.show("modal-agreement-print");
    },
    generateAgreement() {
      if (this.$refs.agreementPrint) {
        this.$refs.agreementPrint.print(this.agreementSurgeryName);
      }
      this.$bvModal.hide("modal-agreement-print");
    },
    printDischargeAgainstAdvice() {
      if (this.$refs.dischargeAgainstAdvicePrint) {
        this.$refs.dischargeAgainstAdvicePrint.print();
      }
    },
    async getPatientSurgeryInfo() {
      this.loading = true;

      await this.getList(
        `patient-surgery/${this.$route.params.id}?include=doctorAssist,surgeryStaff,patientCard,doctors`
      );

      this.loading = false;

      this.patientSurgeryData = this.all_data;
    },
    formatDate(date) {
      if (date instanceof Date) {
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, "0"); // JavaScript months are 0-based
        const day = date.getDate().toString().padStart(2, "0");
        const hours = date.getHours().toString().padStart(2, "0");
        const minutes = date.getMinutes().toString().padStart(2, "0");
        return `${year}-${month}-${day} ${hours}:${minutes}`;
      } else {
        console.log("date is not an instance of Date.");
      }
    },
    async submitChangeSurgery() {
      this.loadingChangeSurgery = true;

      this.params.form = {
        surgery_id: this.selectedSurgery,
        _method: "PUT",
      };

      // Use the id of the current surgery record for the endpoint
      this.params.path = `/patient-surgery/${this.$route.params.id}`;

      try {
        await this.$store.dispatch("put", this.params);
        this.loadingChangeSurgery = false;
        this.$bvModal.hide("modal-change-surgery");
        this.getPatientSurgeryInfo();
        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: {
            title: "Success",
            variant: "success",
            text: "Surgery changed successfully",
          },
        });
      } catch (error) {
        this.loadingChangeSurgery = false;
        if (error.response && error.response.status === 400) {
          Object.keys(error.response.data).forEach((item) => {
            this.$toast({
              component: ToastificationContent,
              position: "top-right",
              props: {
                title: "Validation Error",
                variant: "danger",
                text: error.response.data[item][0],
              },
            });
          });
        }
      }
    },
    async changeSurgery() {
      // Get available surgeries
      await this.getList("surgery?take=100");
      this.availableSurgeriesOptions = this.all_data.data.map((surgery) => ({
        id: surgery.id,
        title: surgery.title,
      }));

      // Show the modal
      this.$bvModal.show("modal-change-surgery");
    },
    // Lab Test Methods
    labTestSerializer(val) {
      return {
        name: val.name,
        test_id: val.id,
        price: val.price,
      };
    },
    formatPrice(price) {
      return price ? `${Number(price).toLocaleString()} IQD` : "0 IQD";
    },
    clearAllTests() {
      this.labCreate.testsList = [];
      this.labCreate.singleTests = [];
      this.labCreate.group = null;
      this.labCreate.sampleId = "";
      this.labCreate.sampleDate = new Date().toISOString().slice(0, 10);
    },
    addTestsToList(e) {
      if (!e || !e.length) return;
      const newTestsList = [...this.labCreate.testsList];
      const existingIds = new Set();
      newTestsList.forEach((test) => {
        const id = test.test_id || test.id;
        if (id) existingIds.add(id);
      });
      const testsToAdd = [];
      e.forEach((newTest) => {
        const testId = newTest.test_id || newTest.id;
        if (testId && !existingIds.has(testId)) {
          const test = {
            test_id: testId,
            name: newTest.name,
            price: newTest.price || 0,
          };
          testsToAdd.push(test);
          existingIds.add(testId);
        }
      });
      if (testsToAdd.length > 0) {
        this.labCreate.testsList = [...newTestsList, ...testsToAdd];
      }
    },
    async selectGroup(e) {
      try {
        if (this.labCreate.lastSelectedGroupId !== null) {
          this.labCreate.testsList = this.labCreate.testsList.filter(
            (test) => test.group_id !== this.labCreate.lastSelectedGroupId
          );
        }
        this.labCreate.lastSelectedGroupId = e;
        await this.getPatient(`/testGroup?filter[group_id]=${e}&include=test`);
        const groupTests = this.all_data?.data || [];
        if (groupTests.length === 0) {
          this.$toast({
            component: ToastificationContent,
            position: "top-right",
            props: {
              title: "No tests found in this group",
              variant: "warning",
            },
          });
        } else {
          const mappedTests = groupTests.map((test) => ({
            group_id: e,
            price: test.test.price,
            test_id: test.test.id,
            name: test.test.name,
          }));
          const existingTestIds = new Set(
            this.labCreate.testsList.map((test) => test.test_id)
          );
          const newGroupTests = mappedTests.filter(
            (newTest) => !existingTestIds.has(newTest.test_id)
          );
          if (newGroupTests.length > 0) {
            this.labCreate.testsList = [
              ...this.labCreate.testsList,
              ...newGroupTests,
            ];
          }
        }
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: {
            title: "Error fetching test group",
            variant: "danger",
          },
        });
      }
    },
    async submitLab() {
      if (this.labTestListFiltered.length === 0) {
        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: {
            title: "Please select at least one test",
            variant: "warning",
          },
        });
        return;
      }
      try {
        const finalData = {
          patient_card_id: this.patientSurgeryData.patient_card.id,
          tests: this.labTestListFiltered.map((test) => {
            if (test.group_id) {
              delete test.group_id;
            }
            return test;
          }),
          sample_id: this.labCreate.sampleId,
        };
        if (this.labCreate.sampleDate) {
          finalData.date = this.labCreate.sampleDate;
        }
        await this.$store.dispatch("create", {
          form: finalData,
          path: "/patientCardTest",
        });
        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: {
            title: "Tests added successfully",
            variant: "success",
          },
        });
        this.clearAllTests();
        this.$bvModal.hide("modal-add-lab-test");
        this.getPatientTests();
      } catch (error) {
        if (error.response?.status === 400) {
          const errors = error.response.data;
          for (const item in errors) {
            this.$toast({
              component: ToastificationContent,
              position: "top-right",
              props: {
                title: "Validation Error",
                variant: "danger",
                text: errors[item][0],
              },
            });
          }
        }
      }
    },
    // Drug Methods
    handleDrugSourceChange(value) {
      this.drugCreate.item_name = "";
      this.drugCreate.item_id = null;
      this.drugCreate.drug_scientific_name_id = null;
    },
    formatDate(dateString) {
      const date = new Date(dateString);
      if (!date || isNaN(date)) {
        return "";
      }
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      return `${year}-${month}-${day} ${hours}:${minutes}:00`;
    },
    async submitDrug() {
      // Validation
      if (!this.drugCreate.source_of_drug) {
        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: {
            title: "Validation Error",
            variant: "danger",
            text: "The source of drug field is required",
          },
        });
        return;
      }

      if (this.drugCreate.source_of_drug === "external") {
        if (!this.drugCreate.item_name) {
          this.$toast({
            component: ToastificationContent,
            position: "top-right",
            props: {
              title: "Validation Error",
              variant: "danger",
              text: "The item name field is required",
            },
          });
          return;
        }
      } else {
        if (!this.drugCreate.item_id) {
          this.$toast({
            component: ToastificationContent,
            position: "top-right",
            props: {
              title: "Validation Error",
              variant: "danger",
              text: "The item field is required",
            },
          });
          return;
        }
        if (!this.drugCreate.number_of_doses) {
          this.$toast({
            component: ToastificationContent,
            position: "top-right",
            props: {
              title: "Validation Error",
              variant: "danger",
              text: "The number of doses field is required",
            },
          });
          return;
        }
        if (!this.drugCreate.duration_between_doses) {
          this.$toast({
            component: ToastificationContent,
            position: "top-right",
            props: {
              title: "Validation Error",
              variant: "danger",
              text: "The duration between doses field is required",
            },
          });
          return;
        }
      }

      const payload = {
        item_id:
          this.drugCreate.source_of_drug === "external"
            ? null
            : this.drugCreate.item_id,
        item_name:
          this.drugCreate.source_of_drug === "external"
            ? this.drugCreate.item_name
            : "",
        source_of_drug: this.drugCreate.source_of_drug,
        patient_card_id: this.patientSurgeryData.patient_card.id,
      };

      // Only add these fields if not external
      if (this.drugCreate.source_of_drug !== "external") {
        payload.number_of_doses = this.drugCreate.number_of_doses;
        payload.duration_between_doses = this.drugCreate.duration_between_doses;
      }

      // Optional fields
      if (this.drugCreate.type) payload.type = this.drugCreate.type;
      if (this.drugCreate.dose) payload.dose = this.drugCreate.dose;
      if (this.drugCreate.start_time) {
        payload.start_time = this.formatDate(this.drugCreate.start_time);
      }
      if (this.drugCreate.description)
        payload.description = this.drugCreate.description;
      this.params.form = payload;
      this.params.path = "/patientCardPrescriptions";
      try {
        await this.$store.dispatch("create", this.params);
        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: { title: this.$t("common.success"), variant: "success" },
        });
        this.drugCreate = {
          item_id: null,
          item_name: "",
          number_of_doses: "",
          duration_between_doses: "",
          source_of_drug: "",
          type: "",
          dose: "",
          start_time: new Date().toISOString(),
          description: "",
          drug_scientific_name_id: null,
        };
        this.$bvModal.hide("modal-add-drug");
        this.getPatientDrugs();
      } catch (error) {
        if (error.response && error.response.status === 400) {
          for (const item in error.response.data) {
            this.$toast({
              component: ToastificationContent,
              position: "top-right",
              props: {
                title: "validation error",
                variant: "danger",
                text: error.response.data[item][0],
              },
            });
          }
        }
      }
    },
    // Radiology Methods
    async submitRadiology() {
      const payload = {
        type: "1",
        url: this.radiologyCreate.url,
        patient_card_id: this.patientSurgeryData.patient_card.id,
        radiology_id: this.radiologyCreate.radiology_id,
      };
      this.params.form = payload;
      this.params.path = "/patientCardRadiology";
      try {
        await this.$store.dispatch("create", this.params);
        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: { title: this.$t("common.success"), variant: "success" },
        });
        this.radiologyCreate = { radiology_id: null, type: "1", url: "" };
        this.$bvModal.hide("modal-add-radiology");
        this.getPatientRadiology();
      } catch (error) {
        if (error.response && error.response.status === 400) {
          for (const item in error.response.data) {
            this.$toast({
              component: ToastificationContent,
              position: "top-right",
              props: {
                title: "validation error",
                variant: "danger",
                text: error.response.data[item][0],
              },
            });
          }
        }
      }
    },
    // Blood Transfusion Methods
    async getTransfusionTypeList() {
      try {
        await this.getList(`/bloodTransfusionTypes`);
        this.transfusionTypes = this.all_data.data || [];
      } catch (error) {
        console.error("Error fetching transfusion types:", error);
      }
    },
    getTransfusionType(id) {
      if (!id || !this.transfusionTypes.length) return "-";
      const transfusionType = this.transfusionTypes.find(
        (type) => type.id === id
      );
      return transfusionType ? transfusionType.name : "-";
    },
    async submitBloodTransfusion() {
      this.loadingBloodTransfusion = true;
      this.errorForm.blood_transfusion_type_id = "";
      this.errorForm.patient_abo = "";
      this.errorForm.patient_rh = "";
      this.errorForm.number_of_units = "";

      if (!this.bloodTransfusionForm.blood_transfusion_type_id) {
        this.errorForm.blood_transfusion_type_id =
          "The blood transfusion type field is required.";
        this.loadingBloodTransfusion = false;
        return;
      }
      if (!this.bloodTransfusionForm.patient_abo) {
        this.errorForm.patient_abo = "The ABO field is required.";
        this.loadingBloodTransfusion = false;
        return;
      }
      if (!this.bloodTransfusionForm.patient_rh) {
        this.errorForm.patient_rh = "The RH field is required.";
        this.loadingBloodTransfusion = false;
        return;
      }

      this.bloodTransfusionForm.patient_card_id =
        this.patientSurgeryData.patient_card.id;

      this.params.form = this.bloodTransfusionForm;
      this.params.path = `/bloodTransfusions`;

      try {
        await this.$store.dispatch("create", this.params);
        this.loadingBloodTransfusion = false;
        this.$bvModal.hide("modal-add-blood-transfusion");
        this.getBloodTransfusions();
        this.bloodTransfusionForm = {
          patient_card_id: "",
          patient_abo: "",
          patient_rh: "",
          blood_transfusion_type_id: "",
          number_of_units: "",
          _method: "POST",
        };
        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: {
            title: "Success",
            variant: "success",
            text: "Blood transfusion added successfully",
          },
        });
      } catch (error) {
        this.loadingBloodTransfusion = false;
        if (error.response && error.response.status === 400) {
          for (const item in error.response.data) {
            this.$toast({
              component: ToastificationContent,
              position: "top-right",
              props: {
                title: "Validation Error",
                variant: "danger",
                text: error.response.data[item][0],
              },
            });
          }
        }
      }
    },
    async getBloodTransfusions() {
      if (!this.patientSurgeryData.patient_card) return;
      try {
        await this.getList(
          `/bloodTransfusions?filter[patient_card_id]=${this.patientSurgeryData.patient_card.id}&include[]=transfusionType`
        );
        this.bloodTransfusions = this.all_data.data || [];
      } catch (error) {
        console.error("Error fetching blood transfusions:", error);
        this.bloodTransfusions = [];
      }
    },
    // Invoice Methods
    async fetchInvoices() {
      if (!this.patientSurgeryData.patient_card) return;
      try {
        const url = `/invoices?filter[patient_card_id]=${this.patientSurgeryData.patient_card.id}&include=patientCard&sort=-id`;
        await this.getList(url);
        this.invoices = Array.isArray(this.all_data?.data)
          ? this.all_data.data
          : [];
      } catch (error) {
        console.error("Error fetching invoices:", error);
        this.invoices = [];
      }
    },
    getInvoiceStatusVariant(status) {
      const map = {
        paid: "success",
        pending: "warning",
        cancelled: "danger",
        partial: "info",
        approved: "success",
        printed: "info",
      };
      return map[status] || "secondary";
    },
    getInvoiceTypeName(type) {
      const typeMap = {
        invoice_out_clinic: "cashiers.invoice-type-out-clinic",
        invoice_nicu: "cashiers.invoice-type-nicu",
        invoice_patient_card_test: "cashiers.invoice-type-patient-card-test",
        refund: "cashiers.invoice-type-refund",
        invoice_insurance_surgery: "cashiers.invoice-type-insurance-surgery",
        invoice_emergency: "cashiers.invoice-type-emergency",
      };
      const translationKey = typeMap[type];
      return translationKey ? this.$t(translationKey) : type;
    },
    // Doctor Notes Methods
    async getDoctorNotes() {
      if (!this.patientSurgeryData || !this.patientSurgeryData.id) return;
      try {
        await this.getList(
          `/patientDoctorNote?filter[patient_card_surgery_id]=${this.patientSurgeryData.id}&include=createdBy`
        );
        this.doctorNotes = this.all_data.data || [];
      } catch (error) {
        console.error("Error fetching doctor notes:", error);
        this.doctorNotes = [];
      }
    },
    getDoctorName(doctorId) {
      if (!doctorId) return "-";
      const doctor = this.surgeryDoctorsOptions.find((d) => d.id === doctorId);
      return doctor ? doctor.name : "-";
    },
    async submitDoctorNote() {
      if (!this.doctorNoteForm.note) {
        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: {
            title: "Validation Error",
            variant: "danger",
            text: "Please fill in the note field",
          },
        });
        return;
      }

      // Validate required data exists
      if (!this.patientSurgeryData || !this.patientSurgeryData.patient_card) {
        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: {
            title: "Validation Error",
            variant: "danger",
            text: "Patient card data is missing",
          },
        });
        return;
      }

      this.loadingDoctorNote = true;

      // Auto-populate patient_id, patient_card_id and patient_card_surgery_id from page data
      const patientId =
        this.patientSurgeryData.patient_card?.patient?.id ||
        this.patientSurgeryData.patient_card?.patient_id;
      const patientCardId = this.patientSurgeryData.patient_card?.id;
      const patientCardSurgeryId = this.patientSurgeryData.id;

      const payload = {
        note: this.doctorNoteForm.note,
        date: this.doctorNoteForm.date
          ? `${this.doctorNoteForm.date} 12:00:00`
          : null,
        patient_id: patientId,
        patient_card_id: patientCardId,
        patient_card_surgery_id: patientCardSurgeryId,
        _method: "POST",
      };

      this.params.form = payload;
      this.params.path = `/patientDoctorNote`;

      try {
        await this.$store.dispatch("create", this.params);
        this.loadingDoctorNote = false;
        this.$bvModal.hide("modal-add-doctor-note");
        this.getDoctorNotes();
        this.doctorNoteForm = {
          date: new Date().toISOString().slice(0, 10),
          note: "",
          patient_id: "",
          patient_card_surgery_id: "",
          _method: "POST",
        };
        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: {
            title: "Success",
            variant: "success",
            text: "Doctor note added successfully",
          },
        });
      } catch (error) {
        this.loadingDoctorNote = false;
        if (error.response && error.response.status === 400) {
          for (const item in error.response.data) {
            this.$toast({
              component: ToastificationContent,
              position: "top-right",
              props: {
                title: "Validation Error",
                variant: "danger",
                text: error.response.data[item][0],
              },
            });
          }
        }
      }
    },
    // Update getPatientSurgeryInfo to fetch additional data
    async getPatientSurgeryInfo() {
      this.loading = true;

      await this.getList(
        `patient-surgery/${this.$route.params.id}?include=doctorAssist,surgeryStaff,patientCard,doctors`
      );

      this.loading = false;

      this.patientSurgeryData = this.all_data;

      // Fetch related data
      if (this.patientSurgeryData.patient_card) {
        await Promise.all([
          this.getBloodTransfusions(),
          this.fetchInvoices(),
          this.getDoctorNotes(),
          this.getPatientTests(),
          this.getPatientDrugs(),
          this.getPatientRadiology(),
        ]);

        // Build surgery doctors options
        if (
          this.patientSurgeryData.doctors_prices &&
          this.patientSurgeryData.doctors_prices.items
        ) {
          this.surgeryDoctorsOptions =
            this.patientSurgeryData.doctors_prices.items.map((item) => ({
              id: item.doctor_id,
              name: item.doctor.name,
            }));
        }
      }
    },
    // Patient Tests Methods
    async getPatientTests() {
      if (!this.patientSurgeryData.patient_card) return;
      try {
        await this.getList(
          `/patientCardTest?filter[patient_card_id]=${this.patientSurgeryData.patient_card.id}&include=test,group,unit&sort=-id&take=100`
        );
        this.patientTests = this.all_data.data || [];
      } catch (error) {
        console.error("Error fetching patient tests:", error);
        this.patientTests = [];
      }
    },
    // Patient Drugs Methods
    async getPatientDrugs() {
      if (!this.patientSurgeryData.patient_card) return;
      try {
        // Try patientCardDrug first (for pharmacy drugs)
        await this.getList(
          `/patientCardDrug?filter[patient_card_id]=${this.patientSurgeryData.patient_card.id}&include=item&sort=-id&take=100`
        );
        const pharmacyDrugs = this.all_data.data || [];

        // Also fetch patientCardPrescriptions (for office/surgery drugs)
        await this.getList(
          `/patientCardPrescriptions?filter[patient_card_id]=${this.patientSurgeryData.patient_card.id}&include=item&sort=-id&take=100`
        );
        const prescriptionDrugs = this.all_data.data || [];

        // Combine both lists
        this.patientDrugs = [...pharmacyDrugs, ...prescriptionDrugs];
      } catch (error) {
        console.error("Error fetching patient drugs:", error);
        this.patientDrugs = [];
      }
    },
    // Patient Radiology Methods
    async getPatientRadiology() {
      if (!this.patientSurgeryData.patient_card) return;
      try {
        await this.getList(
          `/patientCardRadiology?filter[patient_card_id]=${this.patientSurgeryData.patient_card.id}&include=radiology&sort=-id&take=100`
        );
        this.patientRadiology = this.all_data.data || [];
      } catch (error) {
        console.error("Error fetching patient radiology:", error);
        this.patientRadiology = [];
      }
    },
  },
};
</script>

<style scoped lang="scss">
.surgery-info-page {
  .header-card {
    border-radius: 8px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    border: 1px solid #e5e7eb;
  }

  .go-back-btn {
    color: #6b7280;
    transition: all 0.2s ease;

    &:hover {
      color: #111827;
      transform: translateX(-2px);
    }
  }

  .action-buttons {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .main-content-card {
    border-radius: 8px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    border: 1px solid #e5e7eb;
  }

  .info-card {
    border-radius: 8px;
    border: 1px solid #e5e7eb;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
    transition: box-shadow 0.2s ease;

    &:hover {
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }
  }

  .section-header {
    background-color: #f9fafb;
    border-bottom: 1px solid #e5e7eb;
    padding: 0.625rem 1rem;
    display: flex;
    align-items: center;

    h5 {
      font-size: 1rem;
      font-weight: 600;
      color: #111827;
      display: flex;
      align-items: center;
      margin-bottom: 0;
    }

    .header-actions-wrapper {
      width: 100%;
      gap: 1rem;
    }
  }

  .info-list {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .info-row {
    display: flex;
    align-items: flex-start;
    gap: 1rem;
    padding-bottom: 0.75rem;
    border-bottom: 1px solid #f3f4f6;

    &:last-child {
      border-bottom: none;
      padding-bottom: 0;
    }

    .info-label {
      font-size: 0.875rem;
      font-weight: 600;
      color: #6b7280;
      min-width: 180px;
      flex-shrink: 0;
      margin-bottom: 0;
      display: block;
    }

    .info-value {
      font-size: 0.9375rem;
      color: #111827;
      margin-bottom: 0;
      word-break: break-word;
      line-height: 1.4;
      flex: 1;
    }
  }

  .info-item {
    .info-label {
      font-size: 0.75rem;
      font-weight: 600;
      color: #6b7280;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      margin-bottom: 0.25rem;
      display: block;
    }

    .info-value {
      font-size: 0.9375rem;
      color: #111827;
      margin-bottom: 0;
      word-break: break-word;
      line-height: 1.4;
    }
  }

  // PDF styles
  .font-modify {
    letter-spacing: normal !important;
  }

  .pdf-content {
    font-family: "Arial", "Tahoma", "Segoe UI", sans-serif !important;
    position: relative;

    * {
      font-family: "Arial", "Tahoma", "Segoe UI", sans-serif !important;
    }
  }

  .pdf-content {
    position: relative;
    min-height: 100vh;
    z-index: 1;
  }

  /* All content should be above watermark */
  .pdf-content > section,
  .pdf-content > div:not(.watermark-wrapper),
  .pdf-content > h1,
  .pdf-content > h4,
  .pdf-content > p,
  .pdf-content > table,
  .pdf-content > .border {
    position: relative;
    z-index: 10 !important;
    background: transparent;
  }

  /* Watermark should be behind everything */
  .pdf-content .watermark-wrapper {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 0 !important;
  }

  .pdf-table {
    page-break-inside: avoid;
  }

  @media print {
    @page {
    }

    .pdf-table {
      page-break-before: avoid;
      page-break-inside: avoid;
    }
  }

  .pdf-table:first-child {
    margin-top: 1em;
    page-break-inside: avoid;
  }

  @media (max-width: 768px) {
    .action-buttons {
      flex-direction: column;
      width: 100%;

      .btn {
        width: 100%;
        margin-right: 0 !important;
        margin-bottom: 0.5rem;
      }
    }

    .info-row {
      flex-direction: column;
      gap: 0.5rem;

      .info-label {
        min-width: auto;
      }
    }
  }

  // Modern Cards Grid (Shared by all sections)
  .modern-cards-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 1.25rem;
  }

  .modern-card {
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
    border: 2px solid #e2e8f0;
    border-radius: 14px;
    padding: 1.25rem;
    transition: all 0.3s;
    display: flex;
    flex-direction: column;
  }

  .modern-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
    border-color: #667eea;
  }

  .modern-card-header {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 1rem;
  }

  .modern-card-icon {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .modern-card-title {
    font-size: 1rem;
    font-weight: 700;
    color: #0f172a;
    flex: 1;
    line-height: 1.3;
  }

  .modern-card-content {
    flex: 1;
    margin-bottom: 1rem;
  }

  .modern-card-info-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.875rem;
    color: #64748b;
    margin-bottom: 0.5rem;

    &:last-child {
      margin-bottom: 0;
    }

    svg {
      flex-shrink: 0;
      color: #94a3b8;
    }

    a {
      color: #667eea;
      text-decoration: none;
      transition: color 0.2s;

      &:hover {
        color: #5568d3;
        text-decoration: underline;
      }
    }
  }

  .modern-card-note-text {
    font-size: 0.9375rem;
    color: #111827;
    line-height: 1.5;
    word-break: break-word;
    margin-bottom: 0.75rem;
  }

  .modern-card-footer {
    display: flex;
    align-items: center;
    padding-top: 0.75rem;
    border-top: 1px dashed #cbd5e1;
    gap: 0.5rem;
  }

  // No Data Empty State
  .no-data-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem 2rem;
    color: #94a3b8;
  }

  .no-data-empty .empty-icon {
    opacity: 0.3;
    margin-bottom: 1rem;
  }

  .no-data-empty p {
    margin: 0;
    font-size: 1rem;
  }

  // Invoices Section Styles
  .invoices-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 1.25rem;
  }

  .invoice-card {
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
    border: 2px solid #e2e8f0;
    border-radius: 14px;
    padding: 1.25rem;
    transition: all 0.3s;
  }

  .invoice-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
    border-color: #667eea;
  }

  .invoice-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
  }

  .invoice-number {
    font-size: 1rem;
    font-weight: 700;
    color: #0f172a;
  }

  .invoice-status-wrapper {
    display: flex;
    align-items: center;
  }

  .invoice-amount {
    font-size: 1.5rem;
    font-weight: 700;
    color: #22c55e;
    margin-bottom: 0.75rem;
  }

  .invoice-description {
    font-size: 0.875rem;
    color: #64748b;
    margin-bottom: 0.75rem;
    line-height: 1.5;
  }

  .invoice-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-top: 0.75rem;
    border-top: 1px dashed #cbd5e1;
    gap: 0.5rem;
  }

  .invoice-date,
  .invoice-type {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.875rem;
    color: #64748b;
  }

  .no-invoices {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem 2rem;
    color: #94a3b8;
  }

  .no-invoices .empty-icon {
    opacity: 0.3;
    margin-bottom: 1rem;
  }

  .no-invoices p {
    margin: 0;
    font-size: 1rem;
  }

  .count-badge {
    background: #667eea;
    color: white;
    font-size: 0.875rem;
    font-weight: 600;
    padding: 0.25rem 0.625rem;
    border-radius: 12px;
  }

  // Selected Tests Section Styles
  .selected-tests-section {
    margin-bottom: 1.5rem;
  }

  .selected-tests-container {
    border: 1px solid #dee2e6;
    border-radius: 0.25rem;
    padding: 1rem;
    background-color: #f8f9fa;
    min-height: 200px;
    max-height: 400px;
    overflow-y: auto;
  }

  .selected-tests-container .table td {
    vertical-align: middle;
    padding: 0.5rem;
  }

  .selected-tests-container .table thead th {
    font-weight: 600;
    text-transform: uppercase;
    font-size: 0.8rem;
    letter-spacing: 0.5px;
  }
}
</style>
